import {
    authService,
    supabase,
    WorkspaceIntegrationAPI,
    type ConnectionScene
} from '@common/api/authService';
import Nango from '@nangohq/frontend';
import type { AxiosRequestConfig } from 'axios';
import {
    buildWaitingPageHtml,
    getBrowserWaitingPageContent,
} from '@/components/WaitingPage';
import {
    useCallback, useEffect, useMemo, useRef, useState
} from 'react';
import { preserveGoogleAdsExternalAccountNames } from './integrationDisplayHydration';
import { resolveFigmaDisconnectTransport } from './integrationDisconnectRouting';
import {
    advanceWorkspaceIntegrationFetchScope,
    createWorkspaceIntegrationFetchScope,
    hasFreshWorkspaceIntegrationSnapshot,
    invalidateWorkspaceIntegrationFetchScope,
    isSameWorkspaceIntegrationFetchScope,
    type WorkspaceIntegrationFetchScope,
} from './workspaceIntegrationFreshness';

// 集成状态枚举
export type IntegrationStatus = 'ACTIVE' | 'INACTIVE' | 'ERROR';

// 扩展 API 类型，添加 connected_at 字段
export interface WorkspaceIntegration extends WorkspaceIntegrationAPI {
}

interface WorkspaceIntegrationsFetchResult {
    integrations: WorkspaceIntegration[];
    error: Error | null;
}

interface WorkspaceIntegrationsInFlightEntry {
    promise: Promise<WorkspaceIntegrationsFetchResult>;
    superseded: boolean;
}

const workspaceIntegrationsCache = new Map<string, WorkspaceIntegration[]>();
const workspaceIntegrationsInFlight = new Map<string, WorkspaceIntegrationsInFlightEntry>();
const workspaceIntegrationsFetchedAt = new Map<string, number>();
const WORKSPACE_INTEGRATIONS_SILENT_REFRESH_DEDUPE_MS = 5000;

const readCachedWorkspaceIntegrations = (workspaceId?: string): WorkspaceIntegration[] => (
    workspaceId ? (workspaceIntegrationsCache.get(workspaceId) || []) : []
);

const normalizeWorkspaceIntegrationsError = (error: unknown): Error => (
    error instanceof Error
        ? error
        : new Error('Failed to load workspace integrations')
);

type WorkspaceIntegrationTimingKey =
    | 'workspaceIntegrationsApi';

type WorkspaceIntegrationTimingTracker = {
    record: (key: WorkspaceIntegrationTimingKey, elapsedMs: number) => void;
    toLogPayload: () => Record<string, unknown>;
};

const nowForTiming = () => (
    typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? performance.now()
        : Date.now()
);

const createWorkspaceIntegrationTimingTracker = (workspaceId?: string): WorkspaceIntegrationTimingTracker => {
    const startedAt = nowForTiming();
    const timings: Partial<Record<WorkspaceIntegrationTimingKey, number>> = {};

    return {
        record: (key, elapsedMs) => {
            timings[key] = Math.round(elapsedMs);
        },
        toLogPayload: () => ({
            workspaceId,
            totalMs: Math.round(nowForTiming() - startedAt),
            ...timings,
        }),
    };
};

const trackPromiseTiming = async <T,>(
    timingTracker: WorkspaceIntegrationTimingTracker,
    key: WorkspaceIntegrationTimingKey,
    promise: PromiseLike<T>
): Promise<T> => {
    const startedAt = nowForTiming();
    try {
        return await promise;
    } finally {
        timingTracker.record(key, nowForTiming() - startedAt);
    }
};

// 按平台分组的集成状态 - 直接使用 API 数据结构
export interface WorkspaceIntegrationsState {
    shopify: WorkspaceIntegration | null;
    bigCommerce: WorkspaceIntegration | null;
    googleAds: WorkspaceIntegration | null;
    googleAnalytics: WorkspaceIntegration | null;
    tiktokAds: WorkspaceIntegration | null;
    googleSearchConsole: WorkspaceIntegration | null;
    semrush: WorkspaceIntegration | null;
    slack: WorkspaceIntegration | null;
    woocommerce: WorkspaceIntegration | null;
    magento: WorkspaceIntegration | null;
    prestashop: WorkspaceIntegration | null;
    aliexpress: WorkspaceIntegration | null;
    klaviyo: WorkspaceIntegration | null;
    mailchimp: WorkspaceIntegration | null;
    wix: WorkspaceIntegration | null;
    metaAds: WorkspaceIntegration | null;
    facebookPage: WorkspaceIntegration | null;
    instagram: WorkspaceIntegration | null;
    similarweb: WorkspaceIntegration | null;
    ahrefs: WorkspaceIntegration | null;
    omnisend: WorkspaceIntegration | null;
    okendo: WorkspaceIntegration | null;
    insiderOne: WorkspaceIntegration | null;
    emarsys: WorkspaceIntegration | null;
    judgeme: WorkspaceIntegration | null;
    linkedinAds: WorkspaceIntegration | null;
    pinterestAds: WorkspaceIntegration | null;
    xAds: WorkspaceIntegration | null;
    youtube: WorkspaceIntegration | null;
    snapchat: WorkspaceIntegration | null;
    feishu: WorkspaceIntegration | null;
    figma: WorkspaceIntegration | null;
}

export interface DisconnectIntegrationOptions {
    connectionId?: string;
    workspaceIntegrationId?: string;
    shopDomain?: string;
    authType?: string;
    native?: boolean;
}

interface FetchWorkspaceIntegrationsOptions {
    forceRefresh?: boolean;
}

// Hook 返回类型
export interface UseWorkspaceIntegrationsReturn {
    integrations: WorkspaceIntegrationsState;
    allIntegrations: WorkspaceIntegration[];
    isLoading: boolean;
    hasFetchedCurrentWorkspace: boolean;
    error: Error | null;
    refresh: (showLoading?: boolean, options?: FetchWorkspaceIntegrationsOptions) => Promise<WorkspaceIntegration[]>;
    isConnected: (platform: string) => boolean;
    getIntegration: (platform: string) => WorkspaceIntegration | null;
    connect: (
        platform: string,
        connectionConfig?: any,
        options?: {
            onAuthorizationStarted?: () => void | Promise<void>;
            onSuccess?: () => void | Promise<void>;
            onError?: (error: string) => void | Promise<void>;
            onRestriction?: (payload: ConnectRestrictionPayload) => void | Promise<void>;
        }
    ) => Promise<any>;
    disconnect: (platform: string, options?: DisconnectIntegrationOptions) => Promise<void>;
    hasConnectedIntegrations: boolean;
}

interface UseWorkspaceIntegrationsOptions {
    enabled?: boolean;
    requireFreshFetch?: boolean;
}

type ConnectRestrictionPayload = {
    restrictionState?: WorkspaceIntegrationAPI['restrictionState'];
    eligibilityState?: WorkspaceIntegrationAPI['eligibilityState'];
    blockConnect?: boolean;
    upgradeRequired?: boolean;
    allowsFreshTrial?: boolean;
    message?: string | null;
    eligibilityMessage?: string | null;
    trialRemainingDays?: number | null;
    currentWorkspaceId?: string | null;
    currentWorkspaceName?: string | null;
    currentStoreCount?: number | null;
    storeLimit?: number | null;
    upgradeTarget?: 'TEAM' | 'SALES' | null;
};

// API 返回的 provider 名称到内部 key 的映射
const PROVIDER_TO_KEY: Record<string, keyof WorkspaceIntegrationsState> = {
    'shopify': 'shopify',
    'google-ads': 'googleAds',
    'google-analytics': 'googleAnalytics',
    'tiktok-ads': 'tiktokAds',
    'google-search-console': 'googleSearchConsole',
    'semrush': 'semrush',
    'slack': 'slack',
    'bigcommerce': 'bigCommerce',
    'woocommerce': 'woocommerce',
    'magento': 'magento',
    'adobe-commerce': 'magento',  // Nango 返回的 provider
    'prestashop': 'prestashop',
    'presta-shop': 'prestashop',
    'aliexpress': 'aliexpress',
    'klaviyo': 'klaviyo',
    'klaviyo-oauth': 'klaviyo',  // Nango 返回的 provider
    'mailchimp': 'mailchimp',
    'mailchimp-oauth': 'mailchimp',
    'wix': 'wix',
    'meta-ads': 'metaAds',
    'metaads': 'metaAds',
    'facebook-page': 'facebookPage',
    'facebook': 'facebookPage',
    'instagram': 'instagram',
    'similarweb': 'similarweb',
    'ahrefs': 'ahrefs',
    'omnisend': 'omnisend',
    'okendo': 'okendo',
    'insider-one': 'insiderOne',
    'insider-one-oauth': 'insiderOne',
    'insider': 'insiderOne',
    'useinsider': 'insiderOne',
    'emarsys': 'emarsys',
    'emarsys-oauth': 'emarsys',
    'sap-emarsys': 'emarsys',
    'sap-engagement-cloud': 'emarsys',
    'judgeme': 'judgeme',
    'judgeme-oauth': 'judgeme',
    'judge-me': 'judgeme',
    'judge.me': 'judgeme',
    'judge': 'judgeme',
    'linkedin-ads': 'linkedinAds',
    'linkedinads': 'linkedinAds',
    'linkedin': 'linkedinAds',
    'pinterest-ads': 'pinterestAds',
    'pinterestads': 'pinterestAds',
    'pinterest': 'pinterestAds',
    'x-ads': 'xAds',
    'xads': 'xAds',
    'twitter-ads': 'xAds',
    'twitterads': 'xAds',
    'twitter-x': 'xAds',
    'twitterx': 'xAds',
    'twitter': 'xAds',
    'x': 'xAds',
    'youtube-ads': 'youtube',
    'youtubeads': 'youtube',
    'youtube': 'youtube',
    'snapchat-ads': 'snapchat',
    'snapchatads': 'snapchat',
    'snapchat': 'snapchat',
    'feishu': 'feishu',
    'figma': 'figma',
    'figma-oauth': 'figma',
};

const JUDGEME_OAUTH_PROVIDERS = new Set(['judgeme', 'judgeme-oauth', 'judge-me', 'judge.me', 'judge']);

// 平台到 Nango integration ID 的映射
const PLATFORM_TO_NANGO_ID: Record<string, string> = {
    'shopify': 'shopify',
    'google-ads': 'google-ads',
    'google-analytics': 'google-analytics',
    'tiktok-ads': 'tiktok-ads',
    'google-search-console': 'google-search-console',
    'semrush': 'semrush',
    'slack': 'slack',
    'bigcommerce': 'bigcommerce',
    'woocommerce': 'woocommerce',
    'magento': 'adobe-commerce',
    'adobe-commerce': 'adobe-commerce',
    'aliexpress': 'aliexpress',
    'klaviyo': 'klaviyo-oauth',
    'mailchimp': 'mailchimp',
    'similarweb': 'similarweb',
    'ahrefs': 'ahrefs',
    'omnisend': 'omnisend',
    'okendo': 'okendo',
    'insider-one': 'insider-one-oauth',
    'insider-one-oauth': 'insider-one-oauth',
    'insider-one-api-key': 'insider-one',
    'emarsys': 'emarsys-oauth',
    'emarsys-oauth': 'emarsys-oauth',
    'mailchimp-oauth': 'mailchimp',
    'figma': 'figma',
    'figma-oauth': 'figma',
};

const MANUAL_API_KEY_PROVIDER: Record<string, string> = {
    'insider-one-api-key': 'insider-one',
    'presta-shop': 'prestashop',
};

const ZERNIO_PROVIDERS = new Set([
    'meta-ads',
    'metaads',
    'facebook-page',
    'facebook',
    'instagram',
    'linkedin-ads',
    'linkedinads',
    'linkedin',
    'pinterest-ads',
    'pinterestads',
    'pinterest',
    'x-ads',
    'xads',
    'twitter-ads',
    'twitterads',
    'twitter-x',
    'twitterx',
    'twitter',
    'x',
    'youtube-ads',
    'youtubeads',
    'youtube',
    'snapchat-ads',
    'snapchatads',
    'snapchat',
]);

const repeatedBindingMessage = 'This store is already bound to another workspace.';

const isConnectRestrictionPayload = (value: unknown): value is ConnectRestrictionPayload => {
    return Boolean(
        value
        && typeof value === 'object'
        && (
            'blockConnect' in value
            || 'restrictionState' in value
            || 'eligibilityState' in value
            || 'upgradeRequired' in value
            || 'currentWorkspaceName' in value
            || 'currentStoreCount' in value
            || 'storeLimit' in value
        )
    );
};

const getErrorMessage = (error: unknown): string | null => {
    if (typeof error === 'string') return error;
    if (error instanceof Error) return error.message;
    if (error && typeof error === 'object' && 'message' in error) {
        return String((error as { message?: unknown }).message || '');
    }
    return null;
};

const resolveRestrictionFromConnectError = (error: unknown): ConnectRestrictionPayload | null => {
    if (isConnectRestrictionPayload(error)) {
        return error;
    }

    const message = getErrorMessage(error);
    if (!message) {
        return null;
    }

    try {
        const parsed = JSON.parse(message);
        if (isConnectRestrictionPayload(parsed)) {
            return parsed;
        }
    } catch {
        // Ignore non-JSON error messages.
    }

    if (
        /already (?:connected|bound) to another workspace/i.test(message)
        || /switch to that workspace or disconnect it there first/i.test(message)
    ) {
        return {
            restrictionState: 'already_bound',
            blockConnect: true,
            upgradeRequired: false,
            allowsFreshTrial: false,
            message: repeatedBindingMessage,
            eligibilityMessage: repeatedBindingMessage,
        };
    }

    return null;
};

type NangoConnectVerificationState = {
    isVerifying: boolean;
    hasSucceeded: boolean;
};

const createNangoConnectVerificationState = (): NangoConnectVerificationState => ({
    isVerifying: false,
    hasSucceeded: false,
});

const shouldIgnoreNangoConnectClose = (state: NangoConnectVerificationState): boolean => {
    return state.isVerifying || state.hasSucceeded;
};

const isProviderConnected = (
    integrations: WorkspaceIntegration[],
    provider: string
): boolean => {
    const targetKey = PROVIDER_TO_KEY[provider] || provider;
    return integrations.some(
        (integration) => integration.connected
            && ((PROVIDER_TO_KEY[integration.provider] || integration.provider) === targetKey)
    );
};

const isProviderConnectionConnected = (
    integrations: WorkspaceIntegration[],
    provider: string,
    connectionId?: string,
    expectedAccount?: string
): boolean => {
    const targetKey = PROVIDER_TO_KEY[provider] || provider;
    const expectedConnectionId = String(connectionId || '').trim();
    const normalizeIdentity = (value?: string | null) => (
        String(value || '')
            .trim()
            .toLowerCase()
            .replace(/^https?:\/\//, '')
            .replace(/\/.*$/, '')
    );
    const expectedAccountId = normalizeIdentity(expectedAccount);
    return integrations.some((integration) => {
        const integrationKey = PROVIDER_TO_KEY[integration.provider] || integration.provider;
        if (!integration.connected || integrationKey !== targetKey) {
            return false;
        }
        if (!expectedConnectionId) {
            if (!expectedAccountId) {
                return true;
            }
            return (integration.mappings || []).some((mapping: any) => [
                mapping.account_name,
                mapping.account_id,
                mapping.connection_id,
                mapping.external_account_name,
                mapping.externalAccountName,
                mapping.external_account_id,
                mapping.externalAccountId,
            ].some((value) => normalizeIdentity(value) === expectedAccountId));
        }
        return (integration.mappings || []).some((mapping: any) => [
            mapping.id,
            mapping.slugId,
            mapping.slug_id,
            mapping.connection_id,
            mapping.connectionId,
            mapping.nango_connection_id,
            mapping.nangoConnectionId,
        ].some((value) => value != null && String(value) === expectedConnectionId)
            && (!expectedAccountId || [
                mapping.account_name,
                mapping.account_id,
                mapping.connection_id,
                mapping.external_account_name,
                mapping.externalAccountName,
                mapping.external_account_id,
                mapping.externalAccountId,
            ].some((value) => normalizeIdentity(value) === expectedAccountId)));
    });
};

const providerConfigObject = (value: unknown): Record<string, any> => {
    if (!value) {
        return {};
    }
    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value);
            return parsed && typeof parsed === 'object' ? parsed : {};
        } catch {
            return {};
        }
    }
    return typeof value === 'object' ? value as Record<string, any> : {};
};

// Google Ads 账户数据类型
export interface GoogleAdsAccount {
    account_id: string;
    name: string;
    currency_code: string;
    timezone: string;
    is_manager: string;
}

export interface GoogleAdsCustomerGroup {
    account_id: string;
    name: string;
    currency_code: string;
    timezone: string;
    accounts: GoogleAdsAccount[];
}

type SlackOAuthPopupResult = {
    code: string;
    state: string;
};

type NativeOAuthPopupResult = {
    code?: string;
    state?: string;
    flow?: string | null;
    connected?: boolean;
    success?: boolean;
    platform?: string;
    connection?: string | null;
    integration_id?: string | number | null;
    account_name?: string | null;
    error?: string | null;
};

type ZernioOAuthPopupResult = {
    type: 'SUCCESS' | 'ERROR';
    flowId: string;
    platform: string;
    message?: string;
};

type FeishuOAuthPopupResult = {
    type: 'SUCCESS' | 'ERROR';
    provider: 'feishu';
    source?: string;
    message?: string;
    connection?: string | null;
    integration_id?: number | string | null;
    tenant_key?: string | null;
    tenant_name?: string | null;
};

const AUTHORIZATION_POPUP_FEATURES = 'width=1000,height=800';
const NATIVE_OAUTH_RESULT_CHANNEL = 'bustly:native-oauth-result';
const NATIVE_OAUTH_DETACHED_POPUP_GRACE_MS = 60 * 1000;
const nativeOAuthResultChannelName = (flow?: string | null): string => (
    flow ? `${NATIVE_OAUTH_RESULT_CHANNEL}:${flow}` : NATIVE_OAUTH_RESULT_CHANNEL
);

const createNativeOAuthFlowId = (): string => {
    try {
        if (window.crypto?.randomUUID) {
            return window.crypto.randomUUID();
        }
    } catch {
        // Fall back below when browser crypto is unavailable.
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const normalizeZernioUserMessage = (message?: string): string => {
    if (!message) {
        return 'Authorization failed. Please try again.';
    }

    if (/no_facebook_pages/i.test(message)) {
        return 'No Facebook Pages were available for this Meta account. Make sure the Meta user manages a Facebook Page and grants Bustly access to Pages permissions.';
    }
    if (/window was closed|authorization.*closed/i.test(message)) {
        return 'Authorization was cancelled before Bustly could finish connecting the account.';
    }

    return message
        .replace(/Zernio/gi, 'Bustly')
        .replace(/Zerinio/gi, 'Bustly')
        .replace(/zerinio/gi, 'Bustly')
        .replace(/zernio/gi, 'Bustly');
};

const writeAuthorizationLoading = (popup: Window) => {
    try {
        popup.document.open();
        popup.document.write(buildWaitingPageHtml({
            ...getBrowserWaitingPageContent('authorization'),
            documentTitle: 'Bustly Authorization',
        }));
        popup.document.close();
    } catch {
        // Cross-browser popup handling can reject document writes; the auth redirect still proceeds.
    }
};

const buildJudgeMeLoginAuthorizeUrl = (authorizeUrl: string): string => {
    const loginUrl = new URL('https://app.judge.me/login');
    loginUrl.searchParams.set('type', 'shop');
    loginUrl.searchParams.set('redirect_to', authorizeUrl);
    return loginUrl.toString();
};

const openAuthorizationPopup = (blockedMessage: string): Window => {
    const popup = window.open('about:blank', '_blank', AUTHORIZATION_POPUP_FEATURES);
    if (!popup) {
        throw new Error(blockedMessage);
    }
    writeAuthorizationLoading(popup);
    try { popup.focus(); } catch {}
    return popup;
};

const closeAuthorizationPopup = (popup: Window | null | undefined) => {
    try {
        if (popup && !popup.closed) {
            popup.close();
        }
    } catch {
        // Best-effort cleanup only.
    }
};

const isPopupClosed = (popup: Window): boolean => {
    try {
        return popup.closed || typeof popup.closed === 'undefined';
    } catch {
        return false;
    }
};

const createNangoPopupProxy = (popup: Window): Window => {
    let closedByProxy = false;
    let closedBeforeNavigation = false;
    let navigationFailed = false;
    let nangoNavigatedPopup = false;

    const proxy = {
        get closed() {
            if (closedByProxy || closedBeforeNavigation || navigationFailed) {
                return true;
            }

            if (nangoNavigatedPopup) {
                return false;
            }

            return isPopupClosed(popup);
        },
        get location() {
            return popup.location;
        },
        set location(value: string | Location) {
            closedBeforeNavigation = isPopupClosed(popup);
            if (closedBeforeNavigation) {
                return;
            }

            try {
                popup.location.replace(String(value));
                nangoNavigatedPopup = true;
                return;
            } catch {
                try {
                    popup.location = value as any;
                    nangoNavigatedPopup = true;
                    return;
                } catch {
                    navigationFailed = true;
                }
            }
        },
        close() {
            closedByProxy = true;
            closeAuthorizationPopup(popup);
        },
        focus() {
            try { popup.focus(); } catch {}
        },
    };

    return proxy as unknown as Window;
};

const runWithPreopenedNangoPopup = async <T,>(
    popup: Window,
    startAuth: () => Promise<T>
): Promise<T> => {
    // Nango's auth() opens a blank popup synchronously before it awaits its websocket handshake.
    // In Safari that popup must be the one created directly from our user's click handler.
    // Chrome can also report the real Window handle as closed right after the cross-origin
    // Nango redirect, so the SDK receives a small proxy that keeps that handle stable.
    const nangoPopup = createNangoPopupProxy(popup);
    const originalOpen = window.open;
    const patchedOpen = ((url?: string | URL, target?: string, features?: string) => {
        if ((url === '' || url === undefined) && target === '_blank') {
            return nangoPopup;
        }
        return originalOpen.call(window, url as any, target, features);
    }) as typeof window.open;

    window.open = patchedOpen;
    let authPromise!: Promise<T>;
    try {
        authPromise = startAuth();
    } finally {
        window.open = originalOpen;
    }
    return authPromise;
};

const zernioMessageOrigins = (): Set<string> => {
    const origins = new Set<string>();
    if (typeof window !== 'undefined') {
        origins.add(window.location.origin);
    }

    for (const value of [
        import.meta.env.VITE_INTEGRATIONS_API_HOST,
        import.meta.env.VITE_OAUTH_HOST,
        import.meta.env.VITE_API_HOST,
        import.meta.env.VITE_SUPABASE_URL,
    ]) {
        if (!value) continue;
        try {
            origins.add(new URL(value).origin);
        } catch {
            // Optional env values may be unset or relative in local builds.
        }
    }

    return origins;
};

const parseJudgeMeOAuthResult = (href: string): NativeOAuthPopupResult | null => {
    try {
        const callbackUrl = new URL(href);
        const result = callbackUrl.searchParams.get('judgeme_oauth');
        if (!result) return null;
        const error = callbackUrl.searchParams.get('judgeme_error');
        return {
            connected: result === '1',
            success: result === '1',
            flow: callbackUrl.searchParams.get('judgeme_flow'),
            platform: callbackUrl.searchParams.get('judgeme_platform') || undefined,
            connection: callbackUrl.searchParams.get('judgeme_connection'),
            account_name: callbackUrl.searchParams.get('judgeme_account'),
            error,
        };
    } catch {
        return null;
    }
};

const buildJudgeMeOAuthReturnUri = (flowId?: string): string => {
    const url = new URL(`${window.location.origin}/admin/judgeme/oauth/callback`);
    if (flowId) {
        url.searchParams.set('judgeme_flow', flowId);
    }
    return url.toString();
};

const waitForNativeOAuthPopupResult = (
    popup: Window,
    providerLabel = 'Authorization',
    timeoutMs = 300000,
    options: { expectedFlowId?: string; expectedState?: string; detachedPopupGraceMs?: number } = {}
): Promise<NativeOAuthPopupResult> => {
    return new Promise((resolve, reject) => {
        const startedAt = Date.now();
        const allowedOrigins = zernioMessageOrigins();
        const detachedPopupGraceMs = options.detachedPopupGraceMs ?? NATIVE_OAUTH_DETACHED_POPUP_GRACE_MS;
        let settled = false;
        let intervalId = 0;
        let channel: BroadcastChannel | null = null;
        let popupClosedSince: number | null = null;

        const cleanup = () => {
            if (intervalId) window.clearInterval(intervalId);
            window.removeEventListener('message', handleMessage);
            if (channel) {
                channel.close();
                channel = null;
            }
        };

        const resolveOnce = (result: NativeOAuthPopupResult) => {
            if (settled) return;
            settled = true;
            cleanup();
            try { popup.close(); } catch {}
            resolve(result);
        };

        const rejectOnce = (error: Error) => {
            if (settled) return;
            settled = true;
            cleanup();
            try { popup.close(); } catch {}
            reject(error);
        };

        const handleOAuthResultData = (value: unknown) => {
            const data = value && typeof value === 'object'
                ? value as NativeOAuthPopupResult & { type?: string }
                : null;
            if (!data || data.type !== 'bustly:native-oauth-result') return;
            if (options.expectedFlowId && data.flow !== options.expectedFlowId) return;
            if (options.expectedState && data.state && data.state !== options.expectedState) return;
            if (data.error || data.success === false) {
                rejectOnce(new Error(`${providerLabel} authorization failed: ${data.error || 'unknown error'}`));
                return;
            }
            resolveOnce({ ...data, connected: data.connected ?? true, success: data.success ?? true });
        };

        const handleMessage = (event: MessageEvent) => {
            if (!allowedOrigins.has(event.origin)) return;
            handleOAuthResultData(event.data);
        };

        window.addEventListener('message', handleMessage);
        try {
            channel = new BroadcastChannel(nativeOAuthResultChannelName(options.expectedFlowId));
            channel.onmessage = (event) => handleOAuthResultData(event.data);
        } catch {
            channel = null;
        }

        intervalId = window.setInterval(() => {
            try {
                if (popup.closed) {
                    popupClosedSince = popupClosedSince || Date.now();
                    if (Date.now() - popupClosedSince >= detachedPopupGraceMs) {
                        rejectOnce(new Error(`${providerLabel} authorization window was closed`));
                    }
                    return;
                }
                popupClosedSince = null;
            } catch {
                popupClosedSince = null;
            }

            if (Date.now() - startedAt > timeoutMs) {
                rejectOnce(new Error(`${providerLabel} authorization timeout`));
                return;
            }

            try {
                const href = popup.location.href;
                if (!href) {
                    return;
                }

                const callbackUrl = new URL(href);
                const oauthError = callbackUrl.searchParams.get('error');
                const code = callbackUrl.searchParams.get('code');
                const state = callbackUrl.searchParams.get('state');
                const flow = callbackUrl.searchParams.get('judgeme_flow');
                const judgeMeResult = parseJudgeMeOAuthResult(href);

                if (oauthError) {
                    rejectOnce(new Error(`${providerLabel} authorization failed: ${oauthError}`));
                    return;
                }

                if (judgeMeResult) {
                    if (options.expectedFlowId && judgeMeResult.flow !== options.expectedFlowId) {
                        return;
                    }
                    if (judgeMeResult.connected) {
                        resolveOnce(judgeMeResult);
                    } else {
                        rejectOnce(new Error(`${providerLabel} authorization failed: ${judgeMeResult.error || 'unknown error'}`));
                    }
                    return;
                }

                if (code && state) {
                    if (options.expectedState && state !== options.expectedState) {
                        return;
                    }
                    resolveOnce({ code, state, flow });
                }
            } catch {
                // Cross-origin phase, ignore until redirected back.
            }
        }, 500);
    });
};

const waitForSlackOAuthPopupResult = (popup: Window, timeoutMs = 300000): Promise<SlackOAuthPopupResult> => {
    return new Promise((resolve, reject) => {
        const startedAt = Date.now();
        const intervalId = window.setInterval(() => {
            if (popup.closed) {
                window.clearInterval(intervalId);
                reject(new Error('Slack authorization window was closed'));
                return;
            }

            if (Date.now() - startedAt > timeoutMs) {
                window.clearInterval(intervalId);
                popup.close();
                reject(new Error('Slack authorization timeout'));
                return;
            }

            try {
                const href = popup.location.href;
                if (!href) {
                    return;
                }

                const callbackUrl = new URL(href);
                const oauthError = callbackUrl.searchParams.get('error');
                const code = callbackUrl.searchParams.get('code');
                const state = callbackUrl.searchParams.get('state');

                if (oauthError) {
                    window.clearInterval(intervalId);
                    popup.close();
                    reject(new Error(`Slack authorization failed: ${oauthError}`));
                    return;
                }

                if (code && state) {
                    window.clearInterval(intervalId);
                    popup.close();
                    resolve({ code, state });
                }
            } catch {
                // Cross-origin phase, ignore until redirected back.
            }
        }, 500);
    });
};

const waitForZernioOAuthPopupResult = (popup: Window, timeoutMs = 300000): Promise<ZernioOAuthPopupResult> => {
    return new Promise((resolve, reject) => {
        let popupClosedSince: number | null = null;
        const timeout = window.setTimeout(() => {
            window.removeEventListener('message', handleMessage);
            try { popup.close(); } catch {}
            reject(new Error('Authorization timed out. Please try again.'));
        }, timeoutMs);

        const intervalId = window.setInterval(() => {
            if (popup.closed) {
                if (popupClosedSince === null) {
                    popupClosedSince = Date.now();
                    return;
                }
                if (Date.now() - popupClosedSince < 2000) {
                    return;
                }
                window.clearInterval(intervalId);
                window.clearTimeout(timeout);
                window.removeEventListener('message', handleMessage);
                reject(new Error('Authorization window was closed before the connection finished.'));
                return;
            }
            popupClosedSince = null;
        }, 500);

        function cleanup() {
            window.clearTimeout(timeout);
            window.clearInterval(intervalId);
            window.removeEventListener('message', handleMessage);
        }

        function handleMessage(event: MessageEvent) {
            const data = event.data;
            if (event.source !== popup) {
                return;
            }
            if (!zernioMessageOrigins().has(event.origin)) {
                return;
            }
            if (!data || data.source !== 'bustly-connection-hub') {
                return;
            }
            cleanup();
            if (data.type === 'SUCCESS') {
                resolve({
                    type: 'SUCCESS',
                    flowId: data.flowId,
                    platform: data.platform,
                    message: data.message,
                });
                return;
            }
            reject(new Error(normalizeZernioUserMessage(data.message)));
        }

        window.addEventListener('message', handleMessage);
    });
};

const waitForFeishuOAuthPopupResult = (popup: Window, timeoutMs = 300000): Promise<FeishuOAuthPopupResult> => {
    return new Promise((resolve, reject) => {
        let settled = false;
        const timeout = window.setTimeout(() => {
            cleanup();
            try { popup.close(); } catch {}
            reject(new Error('Feishu authorization timed out. Please try again.'));
        }, timeoutMs);

        // Feishu's cross-origin authorization page can make Chromium report
        // popup.closed=true to the opener while the authorization window is
        // still visible. Treat only explicit callback messages or timeout as
        // terminal states for this flow.
        let channel: BroadcastChannel | null = null;
        try {
            channel = new BroadcastChannel('connect');
            channel.addEventListener('message', handleChannelMessage);
        } catch {
            channel = null;
        }

        function cleanup() {
            window.clearTimeout(timeout);
            window.removeEventListener('message', handleMessage);
            if (channel) {
                try {
                    channel.removeEventListener('message', handleChannelMessage);
                    channel.close();
                } catch {}
                channel = null;
            }
        }

        function handleFeishuResult(data: unknown) {
            if (settled) return;
            const payload = data && typeof data === 'object'
                ? data as FeishuOAuthPopupResult
                : null;
            if (!payload || payload.source !== 'bustly-feishu' || payload.provider !== 'feishu') return;
            settled = true;
            cleanup();
            if (payload.type === 'SUCCESS') {
                resolve(payload);
                return;
            }
            reject(new Error(payload.message || 'Feishu authorization failed'));
        }

        function handleChannelMessage(event: MessageEvent) {
            handleFeishuResult(event.data);
        }

        function handleMessage(event: MessageEvent) {
            if (event.source !== popup) return;
            if (!zernioMessageOrigins().has(event.origin)) return;
            handleFeishuResult(event.data);
        }

        window.addEventListener('message', handleMessage);
    });
};

/**
 * 从 workspace API 拉取和管理当前 workspace 的集成状态
 */
export const useWorkspaceIntegrations = (
    workspaceId?: string,
    options: UseWorkspaceIntegrationsOptions = {}
): UseWorkspaceIntegrationsReturn => {
    const { enabled = true, requireFreshFetch = false } = options;
    const initialCachedIntegrations = readCachedWorkspaceIntegrations(workspaceId);
    const [allIntegrations, setAllIntegrations] = useState<WorkspaceIntegration[]>(initialCachedIntegrations);
    const [isLoading, setIsLoading] = useState(enabled && initialCachedIntegrations.length === 0);
    const [error, setError] = useState<Error | null>(null);
    const activeFetchScopeRef = useRef<WorkspaceIntegrationFetchScope>(
        createWorkspaceIntegrationFetchScope(enabled ? workspaceId : undefined)
    );
    const [freshFetchScope, setFreshFetchScope] = useState<WorkspaceIntegrationFetchScope | null>(null);
    const renderedWorkspaceId = enabled && workspaceId ? workspaceId : null;
    const hasFetchedCurrentWorkspace = Boolean(
        renderedWorkspaceId
        && activeFetchScopeRef.current.workspaceId === renderedWorkspaceId
        && hasFreshWorkspaceIntegrationSnapshot(
            activeFetchScopeRef.current,
            freshFetchScope
        )
    );

    /**
     * 移除集成
     */
    const removeIntegration = useCallback((provider: string) => {
        const normalizedProvider = provider.toLowerCase().replace(/[\s_]+/g, '-');
        const providerKey = normalizedProvider.startsWith('figma-') ? 'figma' : normalizedProvider;
        const targetKey = PROVIDER_TO_KEY[providerKey] || providerKey;
        setAllIntegrations(prev => prev.filter((integration) => {
            const normalizedIntegrationProvider = integration.provider.toLowerCase().replace(/[\s_]+/g, '-');
            const integrationProviderKey = normalizedIntegrationProvider.startsWith('figma-')
                ? 'figma'
                : normalizedIntegrationProvider;
            const integrationKey = PROVIDER_TO_KEY[integrationProviderKey] || integrationProviderKey;
            return integrationKey !== targetKey;
        }));
    }, []);

    /**
     * 从 API 拉取当前 workspace 的集成列表
     * @param showLoading - 是否显示 loading 状态，默认为 true
     */
    const fetchIntegrations = useCallback(async (
        showLoading = true,
        options: FetchWorkspaceIntegrationsOptions = {}
    ): Promise<WorkspaceIntegration[]> => {
        const forceRefresh = options.forceRefresh === true;
        const requestScope = activeFetchScopeRef.current;
        const isRequestForActiveWorkspace = () => (
            requestScope.workspaceId === workspaceId
            && isSameWorkspaceIntegrationFetchScope(activeFetchScopeRef.current, requestScope)
        );
        if (!workspaceId) {
            const empty: WorkspaceIntegration[] = [];
            setAllIntegrations(empty);
            setIsLoading(false);
            setError(null);
            return empty;
        }

        const cachedIntegrations = readCachedWorkspaceIntegrations(workspaceId);
        const inFlightEntry = forceRefresh ? undefined : workspaceIntegrationsInFlight.get(workspaceId);
        if (inFlightEntry) {
            if (isRequestForActiveWorkspace() && showLoading && cachedIntegrations.length === 0) {
                setIsLoading(true);
            }
            const result = await inFlightEntry.promise;
            if (inFlightEntry.superseded) {
                if (!isRequestForActiveWorkspace()) {
                    return readCachedWorkspaceIntegrations(workspaceId);
                }
                return fetchIntegrations(showLoading, { forceRefresh: false });
            }

            if (isRequestForActiveWorkspace()) {
                if (result.error) {
                    setError(result.error);
                    setFreshFetchScope(null);
                } else {
                    setAllIntegrations((previousIntegrations) => {
                        const nextIntegrations = preserveGoogleAdsExternalAccountNames(
                            result.integrations,
                            previousIntegrations
                        );
                        workspaceIntegrationsCache.set(workspaceId, nextIntegrations);
                        return nextIntegrations;
                    });
                    setError(null);
                    setFreshFetchScope(requestScope);
                }
                if (showLoading) {
                    setIsLoading(false);
                }
            }

            return result.error ? cachedIntegrations : result.integrations;
        }

        const lastFetchedAt = workspaceIntegrationsFetchedAt.get(workspaceId) || 0;
        const isRecentSilentRefresh = !showLoading
            && !forceRefresh
            && nowForTiming() - lastFetchedAt < WORKSPACE_INTEGRATIONS_SILENT_REFRESH_DEDUPE_MS;
        if (isRecentSilentRefresh) {
            if (isRequestForActiveWorkspace()) {
                setAllIntegrations(cachedIntegrations);
                setError(null);
                setFreshFetchScope(requestScope);
            }
            return cachedIntegrations;
        }

        if (isRequestForActiveWorkspace() && showLoading) {
            setIsLoading(true);
        }
        if (isRequestForActiveWorkspace()) {
            setError(null);
        }

        const timingTracker = createWorkspaceIntegrationTimingTracker(workspaceId);
        const previousEntry = workspaceIntegrationsInFlight.get(workspaceId);
        if (forceRefresh && previousEntry) {
            previousEntry.superseded = true;
        }

        const entry: WorkspaceIntegrationsInFlightEntry = {
            superseded: false,
            promise: (async (): Promise<WorkspaceIntegrationsFetchResult> => {
                try {
                    const integrations: WorkspaceIntegration[] = [];
                    const response = await trackPromiseTiming(
                        timingTracker,
                        'workspaceIntegrationsApi',
                        authService.getWorkspaceIntegrations({
                            headers: { 'X-Workspace-Id': workspaceId },
                        })
                    );

                    // 直接使用 API 返回的数据格式，不做转换
                    // response 已经是 data 对象了（api.ts 自动提取了 response.data.data）
                    if (response) {
                        Object.entries(response).forEach(([, apiIntegrations]) => {
                            if (Array.isArray(apiIntegrations) && apiIntegrations.length > 0) {
                                apiIntegrations.forEach((apiIntegration: WorkspaceIntegrationAPI) => {
                                    integrations.push({ ...apiIntegration });
                                });
                            }
                        });
                    }

                    return { integrations, error: null };
                } catch (requestError) {
                    const normalizedError = normalizeWorkspaceIntegrationsError(requestError);
                    console.error('Error fetching workspace integrations:', normalizedError);
                    return { integrations: [], error: normalizedError };
                } finally {
                    console.info('[useWorkspaceIntegrations] load timings', timingTracker.toLogPayload());
                }
            })(),
        };

        workspaceIntegrationsInFlight.set(workspaceId, entry);
        const result = await entry.promise;
        const shouldApplyGlobally = (
            !entry.superseded
            && workspaceIntegrationsInFlight.get(workspaceId) === entry
        );

        if (shouldApplyGlobally) {
            if (!result.error) {
                const nextIntegrations = preserveGoogleAdsExternalAccountNames(
                    result.integrations,
                    readCachedWorkspaceIntegrations(workspaceId)
                );
                workspaceIntegrationsCache.set(workspaceId, nextIntegrations);
                workspaceIntegrationsFetchedAt.set(workspaceId, nowForTiming());
            }
            workspaceIntegrationsInFlight.delete(workspaceId);
        }

        if (entry.superseded) {
            if (!isRequestForActiveWorkspace()) {
                return readCachedWorkspaceIntegrations(workspaceId);
            }
            return fetchIntegrations(showLoading, { forceRefresh: false });
        }

        if (isRequestForActiveWorkspace()) {
            if (result.error) {
                setError(result.error);
                setFreshFetchScope(null);
            } else {
                setAllIntegrations((previousIntegrations) => {
                    const nextIntegrations = preserveGoogleAdsExternalAccountNames(
                        result.integrations,
                        previousIntegrations
                    );
                    workspaceIntegrationsCache.set(workspaceId, nextIntegrations);
                    return nextIntegrations;
                });
                setError(null);
                setFreshFetchScope(requestScope);
            }
            if (showLoading) {
                setIsLoading(false);
            }
        }

        return result.error ? cachedIntegrations : result.integrations;
    }, [workspaceId]);

    // 初始化和 workspace 变化时拉取数据
    useEffect(() => {
        const activeScope = advanceWorkspaceIntegrationFetchScope(
            activeFetchScopeRef.current,
            enabled ? workspaceId : undefined
        );
        activeFetchScopeRef.current = activeScope;
        setFreshFetchScope(null);

        const cachedIntegrations = readCachedWorkspaceIntegrations(workspaceId);
        if (cachedIntegrations.length > 0) {
            setAllIntegrations(cachedIntegrations);
        } else {
            setAllIntegrations([]);
        }

        if (!enabled) {
            setIsLoading(false);
            setError(null);
            return () => {
                if (isSameWorkspaceIntegrationFetchScope(activeFetchScopeRef.current, activeScope)) {
                    activeFetchScopeRef.current = invalidateWorkspaceIntegrationFetchScope(activeScope);
                }
            };
        }

        const hasCachedIntegrations = cachedIntegrations.length > 0;
        const shouldShowLoading = requireFreshFetch || !hasCachedIntegrations;
        setIsLoading(shouldShowLoading);
        void fetchIntegrations(shouldShowLoading, { forceRefresh: requireFreshFetch });

        return () => {
            if (isSameWorkspaceIntegrationFetchScope(activeFetchScopeRef.current, activeScope)) {
                activeFetchScopeRef.current = invalidateWorkspaceIntegrationFetchScope(activeScope);
            }
        };
    // 依赖 workspace 身份和显式 freshness 策略，避免 callback 变化引起重复请求
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workspaceId, enabled, requireFreshFetch]);


    /**
     * 验证集成是否创建成功
     * 通过直接查询 workspace_integrations 表检查状态
     * @param provider - 集成平台 (如 'shopify', 'google-ads', 'slack', 'bigcommerce')
     * @param connectionId - Nango 连接 ID (可选，Shopify 不需要)
     */
    const checkIntegrationCreated = useCallback(async (
        provider: string,
        connectionId?: string,
        options: { expectedAccount?: string } = {}
    ): Promise<void> => {
        if (!workspaceId) {
            throw new Error('Workspace ID is required');
        }

        const normalizedProvider = provider.toLowerCase().replace(/[\s_]+/g, '-');
        const providerKey = normalizedProvider.startsWith('figma-') ? 'figma' : normalizedProvider;
        if (JUDGEME_OAUTH_PROVIDERS.has(normalizedProvider)) {
            for (let i = 0; i < 8; i++) {
                await new Promise(resolve => setTimeout(resolve, 1000));
                const refreshedIntegrations = await fetchIntegrations(false, { forceRefresh: true });
                const isConnected = connectionId || options.expectedAccount
                    ? isProviderConnectionConnected(refreshedIntegrations, normalizedProvider, connectionId, options.expectedAccount)
                    : isProviderConnected(refreshedIntegrations, normalizedProvider);
                if (isConnected) {
                    return;
                }
            }

            throw new Error(`Failed to create ${provider} integration`);
        }

        // Nango provider 到数据库 platform 的映射
        const PROVIDER_TO_DB_PLATFORM: Record<string, string> = {
            'shopify': 'SHOPIFY',
            'google-ads': 'GOOGLE_ADS',
            'google-analytics': 'GOOGLE_ANALYTICS',
            'tiktok-ads': 'TIKTOK_ADS',
            'google-search-console': 'GOOGLE_SEARCH_CONSOLE',
            'semrush': 'SEMRUSH',
            'slack': 'SLACK',
            'bigcommerce': 'BIGCOMMERCE',
            'woocommerce': 'WOOCOMMERCE',
            'magento': 'MAGENTO',
            'adobe-commerce': 'MAGENTO',  // Nango 的 adobe-commerce 对应数据库的 MAGENTO
            'prestashop': 'PRESTASHOP',
            'presta-shop': 'PRESTASHOP',
            'aliexpress': 'ALIEXPRESS',
            'klaviyo': 'KLAVIYO',
            'klaviyo-oauth': 'KLAVIYO',
            'mailchimp': 'MAILCHIMP',
            'mailchimp-oauth': 'MAILCHIMP',
            'feishu': 'FEISHU',
            'wix': 'WIX',
            'meta-ads': 'META_ADS',
            'metaads': 'META_ADS',
            'facebook-page': 'FACEBOOK_PAGE',
            'facebook': 'FACEBOOK_PAGE',
            'instagram': 'INSTAGRAM',
            'similarweb': 'SIMILARWEB',
            'ahrefs': 'AHREFS',
            'omnisend': 'OMNISEND',
            'okendo': 'OKENDO',
            'insider-one': 'INSIDER_ONE',
            'insider-one-oauth': 'INSIDER_ONE',
            'insider': 'INSIDER_ONE',
            'useinsider': 'INSIDER_ONE',
            'emarsys': 'EMARSYS',
            'emarsys-oauth': 'EMARSYS',
            'sap-emarsys': 'EMARSYS',
            'sap-engagement-cloud': 'EMARSYS',
            'linkedin-ads': 'LINKEDIN_ADS',
            'linkedinads': 'LINKEDIN_ADS',
            'linkedin': 'LINKEDIN_ADS',
            'pinterest-ads': 'PINTEREST_ADS',
            'pinterestads': 'PINTEREST_ADS',
            'pinterest': 'PINTEREST_ADS',
            'x-ads': 'X_ADS',
            'xads': 'X_ADS',
            'twitter-ads': 'X_ADS',
            'twitterads': 'X_ADS',
            'twitter-x': 'X_ADS',
            'twitterx': 'X_ADS',
            'twitter': 'X_ADS',
            'x': 'X_ADS',
            'youtube-ads': 'YOUTUBE',
            'youtubeads': 'YOUTUBE',
            'youtube': 'YOUTUBE',
            'snapchat-ads': 'SNAPCHAT',
            'snapchatads': 'SNAPCHAT',
            'snapchat': 'SNAPCHAT',
            'judgeme': 'JUDGEME',
            'judge-me': 'JUDGEME',
            'judge.me': 'JUDGEME',
            'judge': 'JUDGEME',
            'figma': 'FIGMA',
            'figma-oauth': 'FIGMA',
        };
        const PROVIDER_TO_DB_PLATFORM_ALIASES: Record<string, string[]> = {
            'meta-ads': ['META_ADS'],
            'metaads': ['META_ADS'],
            'facebook-page': ['FACEBOOK_PAGE'],
            'facebook': ['FACEBOOK_PAGE'],
            'instagram': ['INSTAGRAM'],
            'linkedin-ads': ['LINKEDIN_ADS'],
            'linkedinads': ['LINKEDIN_ADS'],
            'linkedin': ['LINKEDIN_ADS'],
            'pinterest-ads': ['PINTEREST_ADS'],
            'pinterestads': ['PINTEREST_ADS'],
            'pinterest': ['PINTEREST_ADS'],
            'x-ads': ['X_ADS'],
            'xads': ['X_ADS'],
            'twitter-ads': ['X_ADS'],
            'twitterads': ['X_ADS'],
            'twitter-x': ['X_ADS'],
            'twitterx': ['X_ADS'],
            'twitter': ['X_ADS'],
            'x': ['X_ADS'],
            'youtube-ads': ['YOUTUBE'],
            'youtubeads': ['YOUTUBE'],
            'youtube': ['YOUTUBE'],
            'snapchat-ads': ['SNAPCHAT'],
            'snapchatads': ['SNAPCHAT'],
            'snapchat': ['SNAPCHAT'],
            'mailchimp': ['MAILCHIMP'],
            'mailchimp-oauth': ['MAILCHIMP'],
            'insider-one': ['INSIDER_ONE'],
            'insider-one-oauth': ['INSIDER_ONE'],
            'insider': ['INSIDER_ONE'],
            'useinsider': ['INSIDER_ONE'],
            'emarsys': ['EMARSYS'],
            'emarsys-oauth': ['EMARSYS'],
            'sap-emarsys': ['EMARSYS'],
            'sap-engagement-cloud': ['EMARSYS'],
            'okendo': ['OKENDO'],
            'judgeme': ['JUDGEME'],
            'judge-me': ['JUDGEME'],
            'judge.me': ['JUDGEME'],
            'judge': ['JUDGEME'],
            'feishu': ['FEISHU'],
            'figma': ['FIGMA'],
            'figma-oauth': ['FIGMA'],
        };

        const platform = PROVIDER_TO_DB_PLATFORM[providerKey] || providerKey.toUpperCase().replace(/-/g, '_');
        const platformCandidates = PROVIDER_TO_DB_PLATFORM_ALIASES[providerKey] || [platform];
        const isZernioProvider = ZERNIO_PROVIDERS.has(providerKey);
        const webhookBackedProviders = new Set(['woocommerce', 'magento', 'adobe-commerce', 'klaviyo', 'klaviyo-oauth', 'mailchimp', 'mailchimp-oauth', 'semrush', 'insider-one', 'insider-one-oauth', 'emarsys', 'emarsys-oauth', 'judgeme', 'judge-me', 'judge.me', 'figma', 'figma-oauth']);
        const delayedActivationProviders = new Set(['shopify']);
        const canWaitForActivation =
            delayedActivationProviders.has(providerKey)
            || webhookBackedProviders.has(providerKey)
            || isZernioProvider;
        const maxAttempts = providerKey === 'google-ads' || isZernioProvider
            ? 45
            : delayedActivationProviders.has(providerKey) ? 45
            : webhookBackedProviders.has(providerKey) ? 8 : 3;

        for (let i = 0; i < maxAttempts; i++) {
            // 等待 1 秒
            await new Promise(resolve => setTimeout(resolve, 1000));

            // 构建 Supabase 查询
            let query = supabase
                .from('workspace_integrations')
                .select('*')
                .eq('workspace_id', workspaceId);

            query = platformCandidates.length > 1
                ? query.in('platform', platformCandidates)
                : query.eq('platform', platformCandidates[0]);

            // Feishu BYO custom app OAuth is native and returns workspace_integrations.slug_id.
            if (connectionId && provider === 'feishu') {
                query = query.eq('slug_id', connectionId);
            } else if (provider === 'feishu') {
                query = query.eq('status', 'ACTIVE');
            }

            // 如果有 connectionId，则添加 nango_connection_id 过滤条件
            if (connectionId) {
                if (provider !== 'feishu') {
                    query = query.eq('nango_connection_id', connectionId);
                }
            }

            const { data, error } = await query
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle();

            if (error) {
                console.error('Error checking integration status:', error);
                continue;
            }

            // 找到记录，检查状态
            if (data) {
                const status = String(data.status || '').toUpperCase();
                if (status === 'ACTIVE') {
                    await fetchIntegrations(false, { forceRefresh: true });
                    return; // 成功则立即返回
                }

                if (status === 'FAILED' || status === 'ERROR') {
                    const errorMsg = data.error_message || `Integration ${provider} failed with status: ${data.status}`;
                    throw new Error(isZernioProvider ? normalizeZernioUserMessage(errorMsg) : errorMsg);
                }

                if (!canWaitForActivation || i === maxAttempts - 1) {
                    // 状态为非 ACTIVE，抛出错误信息
                    const errorMsg = data.error_message || `Integration ${provider} failed with status: ${data.status}`;
                    throw new Error(isZernioProvider ? normalizeZernioUserMessage(errorMsg) : errorMsg);
                }
            }

            if (isZernioProvider) {
                const refreshedIntegrations = await fetchIntegrations(false, { forceRefresh: true });
                if (isProviderConnected(refreshedIntegrations, providerKey)) {
                    return;
                }
            }
        }

        const refreshedIntegrations = await fetchIntegrations(false, { forceRefresh: true });
        if (isProviderConnected(refreshedIntegrations, providerKey)) {
            return;
        }

        throw new Error(`Failed to create ${provider} integration`);
    }, [fetchIntegrations, workspaceId]);

    const checkZernioIntegrationCreated = useCallback(async (
        provider: string,
        flowId?: string,
        axiosRequestConfig?: AxiosRequestConfig,
        options: {
            maxAttempts?: number;
            shouldStop?: () => boolean;
        } = {}
    ): Promise<void> => {
        if (!flowId) {
            await checkIntegrationCreated(provider);
            return;
        }

        const maxAttempts = options.maxAttempts ?? 120;

        for (let i = 0; i < maxAttempts; i++) {
            if (options.shouldStop?.()) {
                throw new Error('Authorization was cancelled before the connection finished.');
            }

            await new Promise(resolve => setTimeout(resolve, 1000));

            if (options.shouldStop?.()) {
                throw new Error('Authorization was cancelled before the connection finished.');
            }

            const status = await authService.getZernioConnectStatus(flowId, axiosRequestConfig);
            const flowStatus = String(status.status || '').toUpperCase();
            const integrationStatus = String(status.integrationStatus || '').toUpperCase();
            const failureMessage = status.integrationErrorMessage
                || status.errorMessage
                || `Integration ${provider} failed with status: ${status.integrationStatus || status.status || 'unknown'}`;

            if (flowStatus === 'FAILED' || flowStatus === 'ERROR' || integrationStatus === 'FAILED' || integrationStatus === 'ERROR') {
                throw new Error(normalizeZernioUserMessage(failureMessage));
            }

            if (flowStatus === 'COMPLETED' || integrationStatus === 'ACTIVE') {
                const refreshedIntegrations = await fetchIntegrations(false, { forceRefresh: true });
                if (isProviderConnected(refreshedIntegrations, provider)) {
                    return;
                }
                if (flowStatus === 'COMPLETED') {
                    return;
                }
            }
        }

        const refreshedIntegrations = await fetchIntegrations(false, { forceRefresh: true });
        if (isProviderConnected(refreshedIntegrations, provider)) {
            return;
        }

        throw new Error(`Failed to create ${provider} integration`);
    }, [checkIntegrationCreated, fetchIntegrations]);

    /**
     * 将集成列表按平台分组，只返回每个平台的第一个已连接集成
     */
    const integrations: WorkspaceIntegrationsState = useMemo(() => {
        const result: WorkspaceIntegrationsState = {
            shopify: null,
            bigCommerce: null,
            googleAds: null,
            googleAnalytics: null,
            tiktokAds: null,
            googleSearchConsole: null,
            semrush: null,
            slack: null,
            woocommerce: null,
            magento: null,
            prestashop: null,
            aliexpress: null,
            klaviyo: null,
            mailchimp: null,
            wix: null,
            metaAds: null,
            facebookPage: null,
            instagram: null,
            similarweb: null,
            ahrefs: null,
            omnisend: null,
            okendo: null,
            insiderOne: null,
            emarsys: null,
            judgeme: null,
            linkedinAds: null,
            pinterestAds: null,
            xAds: null,
            youtube: null,
            snapchat: null,
            feishu: null,
            figma: null,
        };

        allIntegrations.forEach((integration) => {
            const key = PROVIDER_TO_KEY[integration.provider];
            if (key && integration.connected && !result[key]) {
                result[key] = integration;
            }
        });

        return result;
    }, [allIntegrations]);

    /**
     * 检查指定平台是否已连接
     */
    const isConnected = useCallback((platform: string): boolean => {
        const normalizedProvider = platform.toLowerCase().replace(/[\s_]+/g, '-');
        const targetKey = PROVIDER_TO_KEY[normalizedProvider] || normalizedProvider;
        return allIntegrations.some(
            (integration) => integration.connected
                && ((PROVIDER_TO_KEY[integration.provider] || integration.provider) === targetKey)
        );
    }, [allIntegrations]);

    /**
     * 获取指定平台的集成信息
     */
    const getIntegration = useCallback((platform: string): WorkspaceIntegration | null => {
        const normalizedProvider = platform.toLowerCase().replace(/[\s_]+/g, '-');
        const targetKey = PROVIDER_TO_KEY[normalizedProvider] || normalizedProvider;
        return (
            allIntegrations.find(
                (integration) => integration.connected
                    && ((PROVIDER_TO_KEY[integration.provider] || integration.provider) === targetKey)
            ) || null
        );
    }, [allIntegrations]);

    /**
     * 检查是否有任何已连接的集成（排除 slack）
     */
    const hasConnectedIntegrations = useMemo(() => {
        return allIntegrations.some(
            (integration) => integration.connected && integration.provider !== 'slack'
        );
    }, [allIntegrations]);

    const resolveConnectionScene = useCallback((connectionConfig?: any): ConnectionScene => {
        if (connectionConfig?.connection_scene === 'onboarding' || connectionConfig?.connection_scene === 'admin') {
            return connectionConfig.connection_scene;
        }

        return connectionConfig?.onboarding_flow ? 'onboarding' : 'admin';
    }, []);

    const buildSceneRequest = useCallback((connectionConfig?: any) => {
        const connectionScene = resolveConnectionScene(connectionConfig);
        return {
            connection_scene: connectionScene,
            onboarding_flow: connectionConfig?.onboarding_flow ?? connectionScene === 'onboarding',
        };
    }, [resolveConnectionScene]);

    const resolveConnectionLabel = useCallback((connectionConfig?: any) => {
        const label = connectionConfig?.label ?? connectionConfig?.connection_alias;
        return typeof label === 'string' ? label.trim() : '';
    }, []);

    const updateConnectedIntegrationLabel = useCallback(async (
        provider: string,
        connectionConfig?: any,
        match: { connectionId?: string; shopDomain?: string } = {}
    ) => {
        const label = resolveConnectionLabel(connectionConfig);
        if (!workspaceId || !label) {
            return;
        }

        const normalizeIdentity = (value?: string | null) => (
            String(value || '')
                .trim()
                .toLowerCase()
                .replace(/^https?:\/\//, '')
                .replace(/\/$/, '')
        );
        const targetKey = PROVIDER_TO_KEY[provider] || provider;
        const targetConnectionId = normalizeIdentity(match.connectionId);
        const targetShopDomain = normalizeIdentity(match.shopDomain);
        const refreshedIntegrations = await fetchIntegrations(false, { forceRefresh: true });
        const integration = refreshedIntegrations.find(item => (
            item.connected
            && ((PROVIDER_TO_KEY[item.provider] || item.provider) === targetKey)
        ));
        const mapping = integration?.mappings?.find(item => {
            const mappingConnectionId = normalizeIdentity(item.connection_id);
            const mappingAccountName = normalizeIdentity(item.account_name);
            const mappingAccountId = normalizeIdentity(item.account_id);

            if (targetConnectionId && mappingConnectionId === targetConnectionId) {
                return true;
            }

            return Boolean(targetShopDomain)
                && (
                    mappingConnectionId === targetShopDomain
                    || mappingAccountName === targetShopDomain
                    || mappingAccountId === targetShopDomain
                );
        }) || integration?.mappings?.[0];

        if (!mapping?.id) {
            return;
        }

        await authService.updateWorkspaceIntegrationLabel(mapping.id, {
            label,
            axiosRequestConfig: {
                headers: { 'X-Workspace-Id': workspaceId },
            },
        });
        await fetchIntegrations(false, { forceRefresh: true });
    }, [fetchIntegrations, resolveConnectionLabel, workspaceId]);

    /**
     * 连接平台集成
     * @param platform - 平台名称（如 'shopify', 'google-ads', 'slack'）
     * @param connectionConfig - 连接配置（可选，如 store_domain, redirect_url 等）
     * @param options - 回调选项（onSuccess, onError）
     */
    const connect = useCallback(async (
        platform: string,
        connectionConfig?: any,
        options?: {
            onAuthorizationStarted?: () => void | Promise<void>;
            onSuccess?: () => void | Promise<void>;
            onError?: (error: string) => void | Promise<void>;
            onRestriction?: (payload: ConnectRestrictionPayload) => void | Promise<void>;
        }
    ) => {
        if (!workspaceId) {
            throw new Error('Workspace ID is required');
        }

        const axiosRequestConfig: AxiosRequestConfig = { headers: { 'X-Workspace-Id': workspaceId } };

        // 标准化平台名称：同时处理空格和下划线
        const normalizedProvider = platform.toLowerCase().replace(/[\s_]+/g, '-');

        const openGenericNangoOAuth = async ({
            integrationId,
            providerConfigKey,
            verificationProvider,
            displayName,
        }: {
            integrationId: string;
            providerConfigKey: string;
            verificationProvider: string;
            displayName: string;
        }) => {
            const tokenBody = await authService.getNangoConnectToken({
                integration_id: integrationId,
                provider_config_key: providerConfigKey,
                ...buildSceneRequest(connectionConfig),
                axiosRequestConfig
            });
            if (tokenBody.blockConnect) {
                return {
                    success: false,
                    restrictionState: tokenBody.restrictionState ?? tokenBody.eligibilityState,
                    blockConnect: tokenBody.blockConnect,
                    upgradeRequired: tokenBody.upgradeRequired ?? false,
                    eligibilityState: tokenBody.eligibilityState,
                    allowsFreshTrial: tokenBody.allowsFreshTrial,
                    message: tokenBody.message ?? tokenBody.eligibilityMessage,
                    eligibilityMessage: tokenBody.eligibilityMessage,
                    trialRemainingDays: tokenBody.trialRemainingDays,
                    currentWorkspaceId: tokenBody.currentWorkspaceId,
                    currentWorkspaceName: tokenBody.currentWorkspaceName,
                    currentStoreCount: tokenBody.currentStoreCount,
                    storeLimit: tokenBody.storeLimit,
                    upgradeTarget: tokenBody.upgradeTarget,
                };
            }

            const nangoToken = tokenBody.data?.token || tokenBody.result?.token || tokenBody.token;
            if (!nangoToken) {
                throw new Error('Failed to retrieve connect token');
            }

            const nango = new Nango({ connectSessionToken: nangoToken });
            const verificationState = createNangoConnectVerificationState();
            nango.openConnectUI({
                onEvent: (event: any) => {
                    if (event.type === 'connect') {
                        if (verificationState.isVerifying || verificationState.hasSucceeded) {
                            return;
                        }
                        verificationState.isVerifying = true;
                        checkIntegrationCreated(verificationProvider, event.payload.connectionId)
                            .then(async () => {
                                verificationState.hasSucceeded = true;
                                verificationState.isVerifying = false;
                                await options?.onSuccess?.();
                            })
                            .catch(async (err) => {
                                verificationState.isVerifying = false;
                                const restriction = resolveRestrictionFromConnectError(err);
                                if (restriction) {
                                    await options?.onRestriction?.(restriction);
                                    return;
                                }
                                void options?.onError?.(err.message || `${displayName} connection verification failed`);
                            });
                    } else if (event.type === 'error') {
                        void options?.onError?.(event.payload?.message || `${displayName} connection failed`);
                    } else if (event.type === 'abort') {
                        void options?.onError?.(`${displayName} connection was cancelled`);
                    } else if (event.type === 'close') {
                        if (shouldIgnoreNangoConnectClose(verificationState)) {
                            return;
                        }
                        void options?.onError?.(`${displayName} connection dialog was closed`);
                    }
                }
            });

            return {
                uiOpened: true,
                restrictionState: tokenBody.restrictionState ?? tokenBody.eligibilityState,
                blockConnect: tokenBody.blockConnect,
                upgradeRequired: tokenBody.upgradeRequired ?? false,
                eligibilityState: tokenBody.eligibilityState,
                allowsFreshTrial: tokenBody.allowsFreshTrial,
                message: tokenBody.message ?? tokenBody.eligibilityMessage,
                eligibilityMessage: tokenBody.eligibilityMessage,
                trialRemainingDays: tokenBody.trialRemainingDays,
                currentWorkspaceId: tokenBody.currentWorkspaceId,
                currentWorkspaceName: tokenBody.currentWorkspaceName,
                currentStoreCount: tokenBody.currentStoreCount,
                storeLimit: tokenBody.storeLimit,
                upgradeTarget: tokenBody.upgradeTarget,
            };
        };

        switch (normalizedProvider) {
            case 'shopify': {
                // Shopify 连接
                const { shop_domain, redirect_url } = connectionConfig || {};
                if (!shop_domain) {
                    throw new Error('shop_domain is required for Shopify connection');
                }

                const authWindow = openAuthorizationPopup('Unable to open the Shopify authorization window. Please allow pop-ups for Bustly and try again.');

                let res;
                try {
                    res = await authService.connectShopify({
                        shop_domain,
                        redirect_url: redirect_url,
                        ...buildSceneRequest(connectionConfig),
                        axiosRequestConfig
                    });
                } catch (error) {
                    closeAuthorizationPopup(authWindow);
                    const restriction = resolveRestrictionFromConnectError(error);
                    if (restriction) {
                        return { success: false, ...restriction };
                    }
                    throw error;
                }

                if (res.blockConnect) {
                    closeAuthorizationPopup(authWindow);
                    return {
                        success: false,
                        restrictionState: res.restrictionState ?? res.eligibilityState,
                        blockConnect: res.blockConnect,
                        upgradeRequired: res.upgradeRequired ?? false,
                        eligibilityState: res.eligibilityState,
                        allowsFreshTrial: res.allowsFreshTrial,
                        eligibilityMessage: res.eligibilityMessage,
                        message: res.message,
                        trialRemainingDays: res.trialRemainingDays,
                        currentWorkspaceId: res.currentWorkspaceId,
                        currentWorkspaceName: res.currentWorkspaceName,
                        currentStoreCount: res.currentStoreCount,
                        storeLimit: res.storeLimit,
                        upgradeTarget: res.upgradeTarget,
                    };
                }

                if (res.authorizationUrl) {
                    authWindow.location.replace(res.authorizationUrl);
                    await options?.onAuthorizationStarted?.();
                    try { authWindow.focus(); } catch {}

                    // 等待授权完成的消息
                    let callbackRestrictionPayload: ConnectRestrictionPayload | null = null;
                    try {
                        await new Promise<void>((resolve, reject) => {
                            const channel = new BroadcastChannel('connect');
                            const timeout = setTimeout(() => {
                                channel.close();
                                reject(new Error('Shopify authorization timeout'));
                            }, 300000); // 5 分钟超时

                            channel.onmessage = async (e) => {
                                if (e.data.type === 'SUCCESS') {
                                    try {
                                        console.log('connect 成功', e.data);
                                        clearTimeout(timeout);
                                        channel.close();
                                        callbackRestrictionPayload = isConnectRestrictionPayload(e.data.payload)
                                            ? e.data.payload
                                            : null;
                                        
                                        if (!callbackRestrictionPayload?.blockConnect) {
                                            // 验证集成是否创建成功
                                            await checkIntegrationCreated('shopify');
                                        }
                                        if (!callbackRestrictionPayload?.blockConnect) {
                                            await updateConnectedIntegrationLabel('shopify', connectionConfig, { shopDomain: shop_domain }).catch((labelError) => {
                                                console.warn('Failed to update Shopify integration label after connect:', labelError);
                                            });
                                        }
                                        resolve();
                                    } catch (connectError) {
                                        reject(connectError instanceof Error ? connectError : new Error('Shopify authorization failed'));
                                    }
                                } else if (e.data.type === 'ERROR') {
                                    clearTimeout(timeout);
                                    channel.close();
                                    reject(new Error(e.data.message || 'Shopify authorization failed'));
                                }
                            };
                        });
                    } catch (error) {
                        closeAuthorizationPopup(authWindow);
                        const restriction = resolveRestrictionFromConnectError(error);
                        if (restriction) {
                            return { success: false, ...restriction };
                        }
                        throw error;
                    }

                    const finalRestrictionPayload = callbackRestrictionPayload
                        ? (() => {
                            const restrictionPayload = callbackRestrictionPayload as ConnectRestrictionPayload;
                            return {
                                restrictionState: restrictionPayload.restrictionState ?? restrictionPayload.eligibilityState ?? res.restrictionState ?? res.eligibilityState,
                                blockConnect: restrictionPayload.blockConnect ?? res.blockConnect,
                                upgradeRequired: restrictionPayload.upgradeRequired ?? res.upgradeRequired ?? false,
                                eligibilityState: restrictionPayload.eligibilityState ?? res.eligibilityState,
                                allowsFreshTrial: restrictionPayload.allowsFreshTrial ?? res.allowsFreshTrial,
                                eligibilityMessage: restrictionPayload.eligibilityMessage ?? res.eligibilityMessage,
                                message: restrictionPayload.message ?? res.message ?? res.eligibilityMessage,
                                trialRemainingDays: restrictionPayload.trialRemainingDays ?? res.trialRemainingDays,
                                currentWorkspaceId: restrictionPayload.currentWorkspaceId ?? res.currentWorkspaceId,
                                currentWorkspaceName: restrictionPayload.currentWorkspaceName ?? res.currentWorkspaceName,
                                currentStoreCount: restrictionPayload.currentStoreCount ?? res.currentStoreCount,
                                storeLimit: restrictionPayload.storeLimit ?? res.storeLimit,
                                upgradeTarget: restrictionPayload.upgradeTarget ?? res.upgradeTarget,
                            };
                        })()
                        : res;

                    return {
                        success: !finalRestrictionPayload.blockConnect,
                        restrictionState: finalRestrictionPayload.restrictionState ?? finalRestrictionPayload.eligibilityState,
                        blockConnect: finalRestrictionPayload.blockConnect,
                        upgradeRequired: finalRestrictionPayload.upgradeRequired ?? false,
                        eligibilityState: finalRestrictionPayload.eligibilityState,
                        allowsFreshTrial: finalRestrictionPayload.allowsFreshTrial,
                        eligibilityMessage: finalRestrictionPayload.eligibilityMessage,
                        message: finalRestrictionPayload.message,
                        trialRemainingDays: finalRestrictionPayload.trialRemainingDays,
                        currentWorkspaceId: finalRestrictionPayload.currentWorkspaceId,
                        currentWorkspaceName: finalRestrictionPayload.currentWorkspaceName,
                        currentStoreCount: finalRestrictionPayload.currentStoreCount,
                        storeLimit: finalRestrictionPayload.storeLimit,
                        upgradeTarget: finalRestrictionPayload.upgradeTarget,
                    };
                } else {
                    closeAuthorizationPopup(authWindow);
                    throw new Error('Shopify store is not found. Please check the store url.');
                }
            }

            case 'wix': {
                // Wix 连接：支持两种模式
                // 1) redirect_callback（旧模式）通过 BroadcastChannel 回调
                // 2) webhook_polling（新模式）安装后轮询 discover 并完成绑定
                const authWindow = openAuthorizationPopup('Failed to open Wix authorization window. Please allow popups for this site.');

                const redirectUrl = (() => {
                    const url = new URL(window.location.href);
                    url.searchParams.set('connect_success', 'wix');
                    url.searchParams.set('workspace_id', workspaceId);
                    url.searchParams.set('type', 'wix');
                    return url.toString();
                })();

                let res;
                try {
                    res = await authService.initWixConnect({
                        workspace_id: workspaceId,
                        redirect_url: redirectUrl,
                        axiosRequestConfig
                    });
                } catch (error) {
                    authWindow.close();
                    throw error;
                }

                if (res.blockConnect) {
                    authWindow.close();
                    return {
                        success: false,
                        restrictionState: res.restrictionState ?? res.eligibilityState,
                        blockConnect: res.blockConnect,
                        upgradeRequired: res.upgradeRequired ?? false,
                        eligibilityState: res.eligibilityState,
                        allowsFreshTrial: res.allowsFreshTrial,
                        eligibilityMessage: res.eligibilityMessage,
                        message: res.message ?? res.eligibilityMessage,
                        trialRemainingDays: res.trialRemainingDays,
                        currentWorkspaceId: res.currentWorkspaceId,
                        currentWorkspaceName: res.currentWorkspaceName,
                        currentStoreCount: res.currentStoreCount,
                        storeLimit: res.storeLimit,
                        upgradeTarget: res.upgradeTarget,
                    };
                }

                if (!res.install_url) {
                    authWindow.close();
                    throw new Error('Failed to get Wix install URL');
                }

                authWindow.location.href = res.install_url;

                if (res.mode === 'redirect_callback') {
                    await new Promise<void>((resolve, reject) => {
                        const channel = new BroadcastChannel('connect');
                        const timeout = setTimeout(() => {
                            channel.close();
                            reject(new Error('Wix authorization timeout'));
                        }, 300000); // 5 分钟超时

                        channel.onmessage = async (e) => {
                            if (e.data.type === 'SUCCESS' && e.data.provider === 'wix') {
                                clearTimeout(timeout);
                                channel.close();
                                await checkIntegrationCreated('wix');
                                resolve();
                            } else if (e.data.type === 'ERROR' && e.data.provider === 'wix') {
                                clearTimeout(timeout);
                                channel.close();
                                reject(new Error(e.data.message || 'Wix authorization failed'));
                            }
                        };
                    });

                    return { success: true };
                }

                const startedAt = Date.now();
                const timeoutMs = 2 * 60 * 1000;
                const connectStartedAtIso = new Date(startedAt - 60 * 1000).toISOString();
                let discoveredInstanceId: string | null = null;
                let lastDiscoveryError: Error | null = null;
                let triedFallbackWithoutTimeFilter = false;

                while (Date.now() - startedAt < timeoutMs) {
                    const elapsedMs = Date.now() - startedAt;
                    await new Promise(resolve => setTimeout(resolve, 2000));

                    try {
                        const discovery = await authService.discoverWixConnect({
                            workspace_id: workspaceId,
                            not_before: connectStartedAtIso,
                            axiosRequestConfig
                        });

                        if (discovery.instance_id) {
                            discoveredInstanceId = discovery.instance_id;
                            break;
                        }

                        if (discovery.source === 'all_claimed') {
                            throw new Error('This Wix site is already connected to another workspace. Switch to that workspace or disconnect it there first.');
                        }

                        // 30 秒后允许降级：不再要求 webhook 必须是“刚刚新写入”的记录，
                        // 兼容“应用早已安装、未触发新 webhook”场景。
                        if (!triedFallbackWithoutTimeFilter && elapsedMs >= 30 * 1000) {
                            triedFallbackWithoutTimeFilter = true;
                            const fallbackDiscovery = await authService.discoverWixConnect({
                                workspace_id: workspaceId,
                                axiosRequestConfig
                            });
                            if (fallbackDiscovery.instance_id) {
                                discoveredInstanceId = fallbackDiscovery.instance_id;
                                break;
                            }
                            if (fallbackDiscovery.source === 'all_claimed') {
                                throw new Error('This Wix site is already connected to another workspace. Switch to that workspace or disconnect it there first.');
                            }
                        }
                    } catch (error: any) {
                        lastDiscoveryError = error instanceof Error ? error : new Error(String(error));
                    }

                    // 用户手动关窗且 15 秒内仍无实例，提前结束，避免长时间转圈
                    if (authWindow.closed && elapsedMs >= 15 * 1000 && !discoveredInstanceId) {
                        try {
                            const fallbackDiscovery = await authService.discoverWixConnect({
                                workspace_id: workspaceId,
                                axiosRequestConfig
                            });
                            if (fallbackDiscovery.instance_id) {
                                discoveredInstanceId = fallbackDiscovery.instance_id;
                                break;
                            }
                        } catch (error: any) {
                            lastDiscoveryError = error instanceof Error ? error : new Error(String(error));
                        }
                        throw new Error('Wix authorization window was closed before installation data was discovered.');
                    }
                }

                if (!discoveredInstanceId) {
                    throw new Error(lastDiscoveryError?.message || 'Wix installation detected no instance_id. Please confirm App Instance Installed webhook is configured and retry.');
                }

                await authService.completeWixConnect({
                    workspace_id: workspaceId,
                    callback_params: {
                        instanceId: discoveredInstanceId
                    },
                    callback_url: window.location.href,
                    axiosRequestConfig
                });

                await checkIntegrationCreated('wix');
                if (!authWindow.closed) {
                    authWindow.close();
                }

                return { success: true };
            }

            case 'google-ads': {
                // Google Ads 连接
                const authWindow = openAuthorizationPopup('Unable to open the Google Ads authorization window. Please allow pop-ups for Bustly and try again.');
                let tokenBody;
                try {
                    tokenBody = await authService.getNangoConnectToken({
                        integration_id: 'google-ads',
                        ...buildSceneRequest(connectionConfig),
                        axiosRequestConfig
                    });
                } catch (error) {
                    closeAuthorizationPopup(authWindow);
                    throw error;
                }
                if (tokenBody.blockConnect) {
                    closeAuthorizationPopup(authWindow);
                    return {
                        success: false,
                        restrictionState: tokenBody.restrictionState ?? tokenBody.eligibilityState,
                        blockConnect: tokenBody.blockConnect,
                        upgradeRequired: tokenBody.upgradeRequired ?? false,
                        eligibilityState: tokenBody.eligibilityState,
                        allowsFreshTrial: tokenBody.allowsFreshTrial,
                        message: tokenBody.message ?? tokenBody.eligibilityMessage,
                        eligibilityMessage: tokenBody.eligibilityMessage,
                        trialRemainingDays: tokenBody.trialRemainingDays,
                        currentWorkspaceId: tokenBody.currentWorkspaceId,
                        currentWorkspaceName: tokenBody.currentWorkspaceName,
                        currentStoreCount: tokenBody.currentStoreCount,
                        storeLimit: tokenBody.storeLimit,
                        upgradeTarget: tokenBody.upgradeTarget,
                    };
                }
                const nangoToken = tokenBody.data?.token || tokenBody.result?.token || tokenBody.token;
                const requiresAccountSelection = Boolean(
                    tokenBody.requiresAccountSelection
                    || tokenBody.data?.requiresAccountSelection
                    || tokenBody.result?.requiresAccountSelection
                );

                if (!nangoToken) {
                    closeAuthorizationPopup(authWindow);
                    throw new Error('Failed to retrieve connect token');
                }

                const nango = new Nango({ connectSessionToken: nangoToken });
                let result;
                try {
                    result = await runWithPreopenedNangoPopup(authWindow, () => nango.auth('google-ads'));
                } catch (error) {
                    closeAuthorizationPopup(authWindow);
                    throw error;
                }

                // Only proceed if we have a connection ID
                if (result?.connectionId) {
                    // 验证集成是否创建成功
                    await checkIntegrationCreated('google-ads', result.connectionId);
                    return requiresAccountSelection
                        ? { ...result, requiresAccountSelection: true }
                        : result;
                }

                return result;
            }

            case 'google-analytics': {
                // Google Analytics 连接
                const authWindow = openAuthorizationPopup('Unable to open the Google Analytics authorization window. Please allow pop-ups for Bustly and try again.');
                let tokenBody;
                try {
                    tokenBody = await authService.getNangoConnectToken({
                        integration_id: 'google-analytics',
                        ...buildSceneRequest(connectionConfig),
                        axiosRequestConfig
                    });
                } catch (error) {
                    closeAuthorizationPopup(authWindow);
                    throw error;
                }
                if (tokenBody.blockConnect) {
                    closeAuthorizationPopup(authWindow);
                    return {
                        success: false,
                        restrictionState: tokenBody.restrictionState ?? tokenBody.eligibilityState,
                        blockConnect: tokenBody.blockConnect,
                        upgradeRequired: tokenBody.upgradeRequired ?? false,
                        message: tokenBody.message ?? tokenBody.eligibilityMessage,
                    };
                }
                const nangoToken = tokenBody.data?.token || tokenBody.result?.token || tokenBody.token;

                if (!nangoToken) {
                    closeAuthorizationPopup(authWindow);
                    throw new Error('Failed to retrieve connect token');
                }

                const nango = new Nango({ connectSessionToken: nangoToken });
                let result;
                try {
                    result = await runWithPreopenedNangoPopup(authWindow, () => nango.auth('google-analytics'));
                } catch (error) {
                    closeAuthorizationPopup(authWindow);
                    throw error;
                }

                if (result?.connectionId) {
                    await checkIntegrationCreated('google-analytics', result.connectionId);
                }

                return result;
            }

            case 'tiktok-ads': {
                const authWindow = openAuthorizationPopup('Unable to open the TikTok Ads authorization window. Please allow pop-ups for Bustly and try again.');
                let tokenBody;
                try {
                    tokenBody = await authService.getNangoConnectToken({
                        integration_id: 'tiktok-ads',
                        provider_config_key: 'tiktok-ads',
                        ...buildSceneRequest(connectionConfig),
                        axiosRequestConfig
                    });
                } catch (error) {
                    closeAuthorizationPopup(authWindow);
                    throw error;
                }
                if (tokenBody.blockConnect) {
                    closeAuthorizationPopup(authWindow);
                    return {
                        success: false,
                        restrictionState: tokenBody.restrictionState ?? tokenBody.eligibilityState,
                        blockConnect: tokenBody.blockConnect,
                        upgradeRequired: tokenBody.upgradeRequired ?? false,
                        message: tokenBody.message ?? tokenBody.eligibilityMessage,
                    };
                }
                const nangoToken = tokenBody.data?.token || tokenBody.result?.token || tokenBody.token;

                if (!nangoToken) {
                    closeAuthorizationPopup(authWindow);
                    throw new Error('Failed to retrieve connect token');
                }

                const nango = new Nango({ connectSessionToken: nangoToken });
                let result;
                try {
                    result = await runWithPreopenedNangoPopup(authWindow, () => nango.auth('tiktok-ads'));
                } catch (error) {
                    closeAuthorizationPopup(authWindow);
                    throw error;
                }

                if (result?.connectionId) {
                    await checkIntegrationCreated('tiktok-ads', result.connectionId);
                }

                return result;
            }

            case 'google-search-console': {
                const authWindow = openAuthorizationPopup('Unable to open the Google Search Console authorization window. Please allow pop-ups for Bustly and try again.');
                let tokenBody;
                try {
                    tokenBody = await authService.getNangoConnectToken({
                        integration_id: 'google-search-console',
                        provider_config_key: 'google-search-console',
                        ...buildSceneRequest(connectionConfig),
                        axiosRequestConfig
                    });
                } catch (error) {
                    closeAuthorizationPopup(authWindow);
                    throw error;
                }
                if (tokenBody.blockConnect) {
                    closeAuthorizationPopup(authWindow);
                    return {
                        success: false,
                        restrictionState: tokenBody.restrictionState ?? tokenBody.eligibilityState,
                        blockConnect: tokenBody.blockConnect,
                        upgradeRequired: tokenBody.upgradeRequired ?? false,
                        message: tokenBody.message ?? tokenBody.eligibilityMessage,
                    };
                }
                const nangoToken = tokenBody.data?.token || tokenBody.result?.token || tokenBody.token;

                if (!nangoToken) {
                    closeAuthorizationPopup(authWindow);
                    throw new Error('Failed to retrieve connect token');
                }

                const nango = new Nango({ connectSessionToken: nangoToken });
                let result;
                try {
                    result = await runWithPreopenedNangoPopup(authWindow, () => nango.auth('google-search-console'));
                } catch (error) {
                    closeAuthorizationPopup(authWindow);
                    throw error;
                }

                if (result?.connectionId) {
                    await checkIntegrationCreated('google-search-console', result.connectionId);
                }

                return result;
            }

            case 'semrush': {
                const tokenBody = await authService.getNangoConnectToken({
                    integration_id: 'semrush',
                    provider_config_key: 'semrush',
                    ...buildSceneRequest(connectionConfig),
                    axiosRequestConfig
                });
                if (tokenBody.blockConnect) {
                    return {
                        success: false,
                        restrictionState: tokenBody.restrictionState ?? tokenBody.eligibilityState,
                        blockConnect: tokenBody.blockConnect,
                        upgradeRequired: tokenBody.upgradeRequired ?? false,
                        eligibilityState: tokenBody.eligibilityState,
                        allowsFreshTrial: tokenBody.allowsFreshTrial,
                        message: tokenBody.message ?? tokenBody.eligibilityMessage,
                        eligibilityMessage: tokenBody.eligibilityMessage,
                        trialRemainingDays: tokenBody.trialRemainingDays,
                        currentWorkspaceId: tokenBody.currentWorkspaceId,
                        currentWorkspaceName: tokenBody.currentWorkspaceName,
                        currentStoreCount: tokenBody.currentStoreCount,
                        storeLimit: tokenBody.storeLimit,
                        upgradeTarget: tokenBody.upgradeTarget,
                    };
                }
                const nangoToken = tokenBody.data?.token || tokenBody.result?.token || tokenBody.token;

                if (!nangoToken) throw new Error('Failed to retrieve connect token');

                const nango = new Nango({ connectSessionToken: nangoToken });
                const semrushVerificationState = createNangoConnectVerificationState();

                nango.openConnectUI({
                    onEvent: (event: any) => {
                        if (event.type === 'connect') {
                            if (semrushVerificationState.isVerifying || semrushVerificationState.hasSucceeded) {
                                return;
                            }
                            semrushVerificationState.isVerifying = true;
                            checkIntegrationCreated('semrush', event.payload.connectionId)
                                .then(async () => {
                                    semrushVerificationState.hasSucceeded = true;
                                    semrushVerificationState.isVerifying = false;
                                    await options?.onSuccess?.();
                                })
                                .catch(async (err) => {
                                    semrushVerificationState.isVerifying = false;
                                    const restriction = resolveRestrictionFromConnectError(err);
                                    if (restriction) {
                                        await options?.onRestriction?.(restriction);
                                        return;
                                    }
                                    void options?.onError?.(err.message || 'Semrush connection verification failed');
                                });
                        } else if (event.type === 'error') {
                            void options?.onError?.(event.payload?.message || 'Semrush connection failed');
                        } else if (event.type === 'abort') {
                            void options?.onError?.('Semrush connection was cancelled');
                        } else if (event.type === 'close') {
                            if (shouldIgnoreNangoConnectClose(semrushVerificationState)) {
                                return;
                            }
                            void options?.onError?.('Semrush connection dialog was closed');
                        }
                    }
                });

                return {
                    uiOpened: true,
                    restrictionState: tokenBody.restrictionState ?? tokenBody.eligibilityState,
                    blockConnect: tokenBody.blockConnect,
                    upgradeRequired: tokenBody.upgradeRequired ?? false,
                    eligibilityState: tokenBody.eligibilityState,
                    allowsFreshTrial: tokenBody.allowsFreshTrial,
                    message: tokenBody.message ?? tokenBody.eligibilityMessage,
                    eligibilityMessage: tokenBody.eligibilityMessage,
                    trialRemainingDays: tokenBody.trialRemainingDays,
                    currentWorkspaceId: tokenBody.currentWorkspaceId,
                    currentWorkspaceName: tokenBody.currentWorkspaceName,
                    currentStoreCount: tokenBody.currentStoreCount,
                    storeLimit: tokenBody.storeLimit,
                    upgradeTarget: tokenBody.upgradeTarget,
                };
            }

            case 'figma-oauth': {
                const provider = 'figma';
                const tokenBody = await authService.getNangoConnectToken({
                    integration_id: provider,
                    provider_config_key: provider,
                    ...buildSceneRequest(connectionConfig),
                    axiosRequestConfig
                });
                if (tokenBody.blockConnect) {
                    return {
                        success: false,
                        restrictionState: tokenBody.restrictionState ?? tokenBody.eligibilityState,
                        blockConnect: tokenBody.blockConnect,
                        upgradeRequired: tokenBody.upgradeRequired ?? false,
                        eligibilityState: tokenBody.eligibilityState,
                        allowsFreshTrial: tokenBody.allowsFreshTrial,
                        message: tokenBody.message ?? tokenBody.eligibilityMessage,
                        eligibilityMessage: tokenBody.eligibilityMessage,
                        trialRemainingDays: tokenBody.trialRemainingDays,
                        currentWorkspaceId: tokenBody.currentWorkspaceId,
                        currentWorkspaceName: tokenBody.currentWorkspaceName,
                        currentStoreCount: tokenBody.currentStoreCount,
                        storeLimit: tokenBody.storeLimit,
                        upgradeTarget: tokenBody.upgradeTarget,
                    };
                }
                const nangoToken = tokenBody.data?.token || tokenBody.result?.token || tokenBody.token;

                if (!nangoToken) throw new Error('Failed to retrieve connect token');

                const nango = new Nango({ connectSessionToken: nangoToken });
                const figmaVerificationState = createNangoConnectVerificationState();

                nango.openConnectUI({
                    onEvent: (event: any) => {
                        if (event.type === 'connect') {
                            if (figmaVerificationState.isVerifying || figmaVerificationState.hasSucceeded) {
                                return;
                            }
                            figmaVerificationState.isVerifying = true;
                            checkIntegrationCreated(provider, event.payload.connectionId)
                                .then(async () => {
                                    figmaVerificationState.hasSucceeded = true;
                                    figmaVerificationState.isVerifying = false;
                                    await options?.onSuccess?.();
                                })
                                .catch(async (err) => {
                                    figmaVerificationState.isVerifying = false;
                                    const restriction = resolveRestrictionFromConnectError(err);
                                    if (restriction) {
                                        await options?.onRestriction?.(restriction);
                                        return;
                                    }
                                    void options?.onError?.(err.message || 'Figma connection verification failed');
                                });
                        } else if (event.type === 'error') {
                            void options?.onError?.(event.payload?.message || 'Figma connection failed');
                        } else if (event.type === 'abort') {
                            void options?.onError?.('Figma connection was cancelled');
                        } else if (event.type === 'close') {
                            if (shouldIgnoreNangoConnectClose(figmaVerificationState)) {
                                return;
                            }
                            void options?.onError?.('Figma connection dialog was closed');
                        }
                    }
                });

                return {
                    uiOpened: true,
                    restrictionState: tokenBody.restrictionState ?? tokenBody.eligibilityState,
                    blockConnect: tokenBody.blockConnect,
                    upgradeRequired: tokenBody.upgradeRequired ?? false,
                    eligibilityState: tokenBody.eligibilityState,
                    allowsFreshTrial: tokenBody.allowsFreshTrial,
                    message: tokenBody.message ?? tokenBody.eligibilityMessage,
                    eligibilityMessage: tokenBody.eligibilityMessage,
                    trialRemainingDays: tokenBody.trialRemainingDays,
                    currentWorkspaceId: tokenBody.currentWorkspaceId,
                    currentWorkspaceName: tokenBody.currentWorkspaceName,
                    currentStoreCount: tokenBody.currentStoreCount,
                    storeLimit: tokenBody.storeLimit,
                    upgradeTarget: tokenBody.upgradeTarget,
                };
            }

            case 'judgeme':
            case 'judge.me':
            case 'judge-me': {
                const oauthPlatform = 'judgeme';
                const displayName = 'Judge.me';
                const redirectUri = `${window.location.origin}${window.location.pathname}`;
                const flowId = createNativeOAuthFlowId();
                const returnUri = buildJudgeMeOAuthReturnUri(flowId);
                const popup = openAuthorizationPopup(`Failed to open ${displayName} authorization window. Please allow popups for this site.`);

                let connectInit;
                try {
                    connectInit = await authService.initJudgeMeOAuth({
                        workspace_id: workspaceId,
                        platform: oauthPlatform,
                        redirect_uri: redirectUri,
                        return_uri: returnUri,
                        connection_name: connectionConfig?.connection_name || connectionConfig?.connectionName,
                        connection_alias: connectionConfig?.connection_alias || connectionConfig?.connectionAlias || connectionConfig?.label,
                    });
                } catch (error) {
                    closeAuthorizationPopup(popup);
                    throw error;
                }

                if (!connectInit.authorize_url) {
                    closeAuthorizationPopup(popup);
                    throw new Error(`Failed to initialize ${displayName} authorization URL`);
                }

                const popupResult = waitForNativeOAuthPopupResult(popup, displayName, undefined, {
                    expectedFlowId: flowId,
                    expectedState: connectInit.state,
                });
                popup.location.replace(buildJudgeMeLoginAuthorizeUrl(connectInit.authorize_url));
                try { popup.focus(); } catch {}

                let oauthResult: NativeOAuthPopupResult;
                try {
                    oauthResult = await popupResult;
                } catch (error) {
                    const message = getErrorMessage(error) || '';
                    if (!/authorization window was closed|window was closed/i.test(message)) {
                        throw error;
                    }
                    await checkIntegrationCreated(oauthPlatform);
                    return { success: true };
                }
                let connectionId = oauthResult.connection || undefined;
                if (!oauthResult.connected) {
                    if (!oauthResult.code || !oauthResult.state) {
                        throw new Error(`${displayName} authorization did not return an OAuth code`);
                    }
                    const completed = await authService.completeJudgeMeOAuth({
                        workspace_id: workspaceId,
                        platform: oauthPlatform,
                        code: oauthResult.code,
                        state: oauthResult.state,
                        redirect_uri: connectInit.redirect_uri,
                    });
                    connectionId = completed.connection || connectionId;
                }
                await checkIntegrationCreated(oauthPlatform, connectionId);
                return { success: true };
            }

            case 'slack': {
                const fallbackRedirectUri = (() => {
                    const url = new URL(window.location.href);
                    url.searchParams.set('connect_success', 'slack');
                    url.searchParams.set('workspace_id', workspaceId);
                    url.searchParams.set('type', 'slack');
                    return url.toString();
                })();

                const popup = openAuthorizationPopup('Failed to open Slack authorization window. Please allow popups for this site.');

                let connectInit;
                try {
                    connectInit = await authService.initSlackConnect({
                        workspace_id: workspaceId,
                        redirect_uri: connectionConfig?.redirect_uri,
                        return_url: connectionConfig?.return_url ?? fallbackRedirectUri
                    });
                } catch (error) {
                    closeAuthorizationPopup(popup);
                    throw error;
                }

                if (!connectInit.authorize_url) {
                    closeAuthorizationPopup(popup);
                    throw new Error('Failed to initialize Slack authorization URL');
                }

                const popupResult = waitForSlackOAuthPopupResult(popup);
                popup.location.replace(connectInit.authorize_url);
                try { popup.focus(); } catch {}

                const oauthResult = await popupResult;

                await authService.completeSlackConnect({
                    code: oauthResult.code,
                    state: oauthResult.state,
                    redirect_uri: connectInit.redirect_uri
                });

                await checkIntegrationCreated('slack');
                return { success: true };
            }

            case 'bigcommerce': {
                // BigCommerce 连接
                const requestBody = {
                    integration_id: 'bigcommerce',
                    ...connectionConfig,
                    ...buildSceneRequest(connectionConfig),
                    axiosRequestConfig
                };

                const authWindow = openAuthorizationPopup('Unable to open the BigCommerce authorization window. Please allow pop-ups for Bustly and try again.');

                let tokenBody;
                try {
                    tokenBody = await authService.getNangoConnectToken(requestBody);
                } catch (error) {
                    closeAuthorizationPopup(authWindow);
                    throw error;
                }

                if (tokenBody.blockConnect) {
                    closeAuthorizationPopup(authWindow);
                    return {
                        success: false,
                        restrictionState: tokenBody.restrictionState ?? tokenBody.eligibilityState,
                        blockConnect: tokenBody.blockConnect,
                        upgradeRequired: tokenBody.upgradeRequired ?? false,
                        eligibilityState: tokenBody.eligibilityState,
                        allowsFreshTrial: tokenBody.allowsFreshTrial,
                        eligibilityMessage: tokenBody.eligibilityMessage,
                        message: tokenBody.message,
                        trialRemainingDays: tokenBody.trialRemainingDays,
                        currentWorkspaceId: tokenBody.currentWorkspaceId,
                        currentWorkspaceName: tokenBody.currentWorkspaceName,
                        currentStoreCount: tokenBody.currentStoreCount,
                        storeLimit: tokenBody.storeLimit,
                        upgradeTarget: tokenBody.upgradeTarget,
                    };
                }

                // 处理重定向（App 安装）
                if (tokenBody.action === 'REDIRECT' && tokenBody.url) {
                    closeAuthorizationPopup(authWindow);
                    return {
                        action: 'REDIRECT',
                        url: tokenBody.url,
                        restrictionState: tokenBody.restrictionState ?? tokenBody.eligibilityState,
                        blockConnect: tokenBody.blockConnect,
                        upgradeRequired: tokenBody.upgradeRequired ?? false,
                        eligibilityState: tokenBody.eligibilityState,
                        allowsFreshTrial: tokenBody.allowsFreshTrial,
                        eligibilityMessage: tokenBody.eligibilityMessage,
                        message: tokenBody.message,
                        trialRemainingDays: tokenBody.trialRemainingDays,
                        currentStoreCount: tokenBody.currentStoreCount,
                        storeLimit: tokenBody.storeLimit,
                        upgradeTarget: tokenBody.upgradeTarget,
                    };
                }

                // 处理 Token 响应（连接）
                const nangoToken = tokenBody.data?.token || tokenBody.result?.token || tokenBody.token;
                if (!nangoToken) {
                    closeAuthorizationPopup(authWindow);
                    throw new Error('未获取到 Token');
                }

                const nango = new Nango({ connectSessionToken: nangoToken });

                if (!connectionConfig?.store_hash || !connectionConfig?.accountUuid) {
                    closeAuthorizationPopup(authWindow);
                    throw new Error('BigCommerce requires store hash and account UUID');
                }

                const authParams = {
                    params: {
                        storeHash: connectionConfig.store_hash,
                        accountUuid: connectionConfig.accountUuid
                    }
                };

                try {
                    await options?.onAuthorizationStarted?.();
                    // @ts-ignore
                    const result = await runWithPreopenedNangoPopup(authWindow, () => nango.auth('bigcommerce', undefined, authParams as any));

                    if (result?.connectionId) {
                        // 验证集成是否创建成功
                        await checkIntegrationCreated('bigcommerce', result.connectionId);
                        await updateConnectedIntegrationLabel('bigcommerce', connectionConfig, { connectionId: result.connectionId });
                    }

                    return {
                        ...result,
                        restrictionState: tokenBody.restrictionState ?? tokenBody.eligibilityState,
                        blockConnect: tokenBody.blockConnect,
                        upgradeRequired: tokenBody.upgradeRequired ?? false,
                        eligibilityState: tokenBody.eligibilityState,
                        allowsFreshTrial: tokenBody.allowsFreshTrial,
                        eligibilityMessage: tokenBody.eligibilityMessage,
                        message: tokenBody.message,
                        trialRemainingDays: tokenBody.trialRemainingDays,
                        currentStoreCount: tokenBody.currentStoreCount,
                        storeLimit: tokenBody.storeLimit,
                        upgradeTarget: tokenBody.upgradeTarget,
                    };
                } catch (error) {
                    closeAuthorizationPopup(authWindow);
                    const restriction = resolveRestrictionFromConnectError(error);
                    if (restriction) {
                        return { success: false, ...restriction };
                    }
                    throw error;
                }
            }

            case 'woocommerce': {
                // WooCommerce 连接 - 使用 Nango Connect UI
                const tokenBody = await authService.getNangoConnectToken({
                    integration_id: 'woocommerce',
                    ...buildSceneRequest(connectionConfig),
                    axiosRequestConfig
                });
                if (tokenBody.blockConnect) {
                    return {
                        success: false,
                        restrictionState: tokenBody.restrictionState ?? tokenBody.eligibilityState,
                        blockConnect: tokenBody.blockConnect,
                        upgradeRequired: tokenBody.upgradeRequired ?? false,
                        eligibilityState: tokenBody.eligibilityState,
                        allowsFreshTrial: tokenBody.allowsFreshTrial,
                        message: tokenBody.message ?? tokenBody.eligibilityMessage,
                        eligibilityMessage: tokenBody.eligibilityMessage,
                        trialRemainingDays: tokenBody.trialRemainingDays,
                        currentWorkspaceId: tokenBody.currentWorkspaceId,
                        currentWorkspaceName: tokenBody.currentWorkspaceName,
                        currentStoreCount: tokenBody.currentStoreCount,
                        storeLimit: tokenBody.storeLimit,
                        upgradeTarget: tokenBody.upgradeTarget,
                    };
                }
                const nangoToken = tokenBody.data?.token || tokenBody.result?.token || tokenBody.token;

                if (!nangoToken) throw new Error('Failed to retrieve connect token');

                const nango = new Nango({ connectSessionToken: nangoToken });
                const wooCommerceVerificationState = createNangoConnectVerificationState();

                // 使用 openConnectUI 打开授权界面
                nango.openConnectUI({
                    onEvent: (event: any) => {
                        if (event.type === 'connect') {
                            if (wooCommerceVerificationState.isVerifying || wooCommerceVerificationState.hasSucceeded) {
                                return;
                            }
                            wooCommerceVerificationState.isVerifying = true;
                            // 连接成功，验证集成是否创建成功
                                checkIntegrationCreated('woocommerce', event.payload.connectionId)
                                    .then(async () => {
                                        wooCommerceVerificationState.hasSucceeded = true;
                                        wooCommerceVerificationState.isVerifying = false;
                                        await options?.onSuccess?.();
                                    })
                                    .catch(async (err) => {
                                        wooCommerceVerificationState.isVerifying = false;
                                        const restriction = resolveRestrictionFromConnectError(err);
                                        if (restriction) {
                                            await options?.onRestriction?.(restriction);
                                            return;
                                        }
                                        void options?.onError?.(err.message || 'WooCommerce connection verification failed');
                                    });
                        } else if (event.type === 'error') {
                            void options?.onError?.(event.payload?.message || 'WooCommerce connection failed');
                        } else if (event.type === 'abort') {
                            void options?.onError?.('WooCommerce connection was cancelled');
                        } else if (event.type === 'close') {
                            if (shouldIgnoreNangoConnectClose(wooCommerceVerificationState)) {
                                return;
                            }
                            // 用户关闭了授权弹窗
                            void options?.onError?.('WooCommerce connection dialog was closed');
                        }
                    }
                });

                // openConnectUI 不会返回结果，而是通过 onEvent 回调处理
                return {
                    uiOpened: true,
                    restrictionState: tokenBody.restrictionState ?? tokenBody.eligibilityState,
                    blockConnect: tokenBody.blockConnect,
                    upgradeRequired: tokenBody.upgradeRequired ?? false,
                    eligibilityState: tokenBody.eligibilityState,
                    allowsFreshTrial: tokenBody.allowsFreshTrial,
                    message: tokenBody.message ?? tokenBody.eligibilityMessage,
                    eligibilityMessage: tokenBody.eligibilityMessage,
                    trialRemainingDays: tokenBody.trialRemainingDays,
                    currentWorkspaceId: tokenBody.currentWorkspaceId,
                    currentWorkspaceName: tokenBody.currentWorkspaceName,
                    currentStoreCount: tokenBody.currentStoreCount,
                    storeLimit: tokenBody.storeLimit,
                    upgradeTarget: tokenBody.upgradeTarget,
                };
            }

            case 'magento':
            case 'adobe-commerce': {
                // Magento (Adobe Commerce) 连接 - 使用 Nango Connect UI
                const tokenBody = await authService.getNangoConnectToken({
                    integration_id: 'adobe-commerce',
                    provider_config_key: 'adobe-commerce',
                    ...buildSceneRequest(connectionConfig),
                    axiosRequestConfig
                });
                if (tokenBody.blockConnect) {
                    return {
                        success: false,
                        restrictionState: tokenBody.restrictionState ?? tokenBody.eligibilityState,
                        blockConnect: tokenBody.blockConnect,
                        upgradeRequired: tokenBody.upgradeRequired ?? false,
                        eligibilityState: tokenBody.eligibilityState,
                        allowsFreshTrial: tokenBody.allowsFreshTrial,
                        message: tokenBody.message ?? tokenBody.eligibilityMessage,
                        eligibilityMessage: tokenBody.eligibilityMessage,
                        trialRemainingDays: tokenBody.trialRemainingDays,
                        currentWorkspaceId: tokenBody.currentWorkspaceId,
                        currentWorkspaceName: tokenBody.currentWorkspaceName,
                        currentStoreCount: tokenBody.currentStoreCount,
                        storeLimit: tokenBody.storeLimit,
                        upgradeTarget: tokenBody.upgradeTarget,
                    };
                }
                const nangoToken = tokenBody.data?.token || tokenBody.result?.token || tokenBody.token;

                if (!nangoToken) throw new Error('Failed to retrieve connect token');

                const nango = new Nango({ connectSessionToken: nangoToken });
                const magentoVerificationState = createNangoConnectVerificationState();

                // 使用 openConnectUI 打开授权界面
                nango.openConnectUI({
                    onEvent: (event: any) => {
                        if (event.type === 'connect') {
                            if (magentoVerificationState.isVerifying || magentoVerificationState.hasSucceeded) {
                                return;
                            }
                            magentoVerificationState.isVerifying = true;
                            // 连接成功，验证集成是否创建成功
                                checkIntegrationCreated('adobe-commerce', event.payload.connectionId)
                                    .then(async () => {
                                        magentoVerificationState.hasSucceeded = true;
                                        magentoVerificationState.isVerifying = false;
                                        await options?.onSuccess?.();
                                    })
                                    .catch(async (err) => {
                                        magentoVerificationState.isVerifying = false;
                                        const restriction = resolveRestrictionFromConnectError(err);
                                        if (restriction) {
                                            await options?.onRestriction?.(restriction);
                                            return;
                                        }
                                        void options?.onError?.(err.message || 'Magento connection verification failed');
                                    });
                        } else if (event.type === 'error') {
                            void options?.onError?.(event.payload?.message || 'Magento connection failed');
                        } else if (event.type === 'abort') {
                            void options?.onError?.('Magento connection was cancelled');
                        } else if (event.type === 'close') {
                            if (shouldIgnoreNangoConnectClose(magentoVerificationState)) {
                                return;
                            }
                            // 用户关闭了授权弹窗
                            void options?.onError?.('Magento connection dialog was closed');
                        }
                    }
                });

                // openConnectUI 不会返回结果，而是通过 onEvent 回调处理
                return {
                    uiOpened: true,
                    restrictionState: tokenBody.restrictionState ?? tokenBody.eligibilityState,
                    blockConnect: tokenBody.blockConnect,
                    upgradeRequired: tokenBody.upgradeRequired ?? false,
                    eligibilityState: tokenBody.eligibilityState,
                    allowsFreshTrial: tokenBody.allowsFreshTrial,
                    message: tokenBody.message ?? tokenBody.eligibilityMessage,
                    eligibilityMessage: tokenBody.eligibilityMessage,
                    trialRemainingDays: tokenBody.trialRemainingDays,
                    currentWorkspaceId: tokenBody.currentWorkspaceId,
                    currentWorkspaceName: tokenBody.currentWorkspaceName,
                    currentStoreCount: tokenBody.currentStoreCount,
                    storeLimit: tokenBody.storeLimit,
                    upgradeTarget: tokenBody.upgradeTarget,
                };
            }

            case 'aliexpress': {
                // Aliexpress 连接 - 获取授权 URL 并打开新窗口
                const authWindow = openAuthorizationPopup('Failed to open authorization window. Please allow popups for this site.');
                let res;
                try {
                    res = await authService.authorizeAliexpress({
                        axiosRequestConfig
                    });
                } catch (error) {
                    closeAuthorizationPopup(authWindow);
                    throw error;
                }

                if (res.auth_url) {
                    authWindow.location.replace(res.auth_url);
                    try { authWindow.focus(); } catch {}

                    // 等待窗口关闭，然后检查连接状态
                    await new Promise<void>((resolve, reject) => {
                        const checkInterval = setInterval(async () => {
                            if (authWindow.closed) {
                                clearInterval(checkInterval);
                                // 窗口关闭后，检查一次连接状态
                                const integrations = await fetchIntegrations(false, { forceRefresh: true });
                                const aliexpressIntegration = integrations.find(i => i.provider === 'aliexpress');

                                if (aliexpressIntegration?.connected) {
                                    // 授权成功
                                    resolve();
                                } else {
                                    // 用户取消授权
                                    reject(new Error('Aliexpress authorization window was closed'));
                                }
                            }
                        }, 500);
                    });

                    return { success: true };
                } else {
                    closeAuthorizationPopup(authWindow);
                    throw new Error('Failed to get Aliexpress authorization URL');
                }
            }

            case 'mailchimp':
            case 'mailchimp-oauth': {
                const tokenBody = await authService.getNangoConnectToken({
                    integration_id: 'mailchimp',
                    provider_config_key: 'mailchimp',
                    ...buildSceneRequest(connectionConfig),
                    axiosRequestConfig
                });
                if (tokenBody.blockConnect) {
                    return {
                        success: false,
                        restrictionState: tokenBody.restrictionState ?? tokenBody.eligibilityState,
                        blockConnect: tokenBody.blockConnect,
                        upgradeRequired: tokenBody.upgradeRequired ?? false,
                        eligibilityState: tokenBody.eligibilityState,
                        allowsFreshTrial: tokenBody.allowsFreshTrial,
                        message: tokenBody.message ?? tokenBody.eligibilityMessage,
                        eligibilityMessage: tokenBody.eligibilityMessage,
                        trialRemainingDays: tokenBody.trialRemainingDays,
                        currentWorkspaceId: tokenBody.currentWorkspaceId,
                        currentWorkspaceName: tokenBody.currentWorkspaceName,
                    };
                }
                const nangoToken = tokenBody.data?.token || tokenBody.result?.token || tokenBody.token;

                if (!nangoToken) throw new Error('Failed to retrieve connect token');

                const nango = new Nango({ connectSessionToken: nangoToken });
                const mailchimpVerificationState = createNangoConnectVerificationState();

                nango.openConnectUI({
                    onEvent: (event: any) => {
                        if (event.type === 'connect') {
                            if (mailchimpVerificationState.isVerifying || mailchimpVerificationState.hasSucceeded) {
                                return;
                            }
                            mailchimpVerificationState.isVerifying = true;
                            checkIntegrationCreated('mailchimp', event.payload.connectionId)
                                .then(async () => {
                                    mailchimpVerificationState.hasSucceeded = true;
                                    mailchimpVerificationState.isVerifying = false;
                                    await options?.onSuccess?.();
                                })
                                .catch(async (err) => {
                                    mailchimpVerificationState.isVerifying = false;
                                    const restriction = resolveRestrictionFromConnectError(err);
                                    if (restriction) {
                                        await options?.onRestriction?.(restriction);
                                        return;
                                    }
                                    void options?.onError?.(err.message || 'Mailchimp connection verification failed');
                                });
                        } else if (event.type === 'error') {
                            void options?.onError?.(event.payload?.message || 'Mailchimp connection failed');
                        } else if (event.type === 'abort') {
                            void options?.onError?.('Mailchimp connection was cancelled');
                        } else if (event.type === 'close') {
                            if (shouldIgnoreNangoConnectClose(mailchimpVerificationState)) {
                                return;
                            }
                            void options?.onError?.('Mailchimp connection dialog was closed');
                        }
                    }
                });

                return {
                    uiOpened: true,
                    restrictionState: tokenBody.restrictionState ?? tokenBody.eligibilityState,
                    blockConnect: tokenBody.blockConnect,
                    upgradeRequired: tokenBody.upgradeRequired ?? false,
                    eligibilityState: tokenBody.eligibilityState,
                    allowsFreshTrial: tokenBody.allowsFreshTrial,
                    message: tokenBody.message ?? tokenBody.eligibilityMessage,
                    eligibilityMessage: tokenBody.eligibilityMessage,
                    trialRemainingDays: tokenBody.trialRemainingDays,
                    currentWorkspaceId: tokenBody.currentWorkspaceId,
                    currentWorkspaceName: tokenBody.currentWorkspaceName,
                };
            }

            case 'klaviyo': {
                // Klaviyo 连接 - 使用 Nango Connect UI
                const tokenBody = await authService.getNangoConnectToken({
                    integration_id: 'klaviyo-oauth',
                    provider_config_key: 'klaviyo-oauth',
                    ...buildSceneRequest(connectionConfig),
                    axiosRequestConfig
                });
                if (tokenBody.blockConnect) {
                    return {
                        success: false,
                        restrictionState: tokenBody.restrictionState ?? tokenBody.eligibilityState,
                        blockConnect: tokenBody.blockConnect,
                        upgradeRequired: tokenBody.upgradeRequired ?? false,
                        eligibilityState: tokenBody.eligibilityState,
                        allowsFreshTrial: tokenBody.allowsFreshTrial,
                        message: tokenBody.message ?? tokenBody.eligibilityMessage,
                        eligibilityMessage: tokenBody.eligibilityMessage,
                        trialRemainingDays: tokenBody.trialRemainingDays,
                        currentWorkspaceId: tokenBody.currentWorkspaceId,
                        currentWorkspaceName: tokenBody.currentWorkspaceName,
                    };
                }
                const nangoToken = tokenBody.data?.token || tokenBody.result?.token || tokenBody.token;

                if (!nangoToken) throw new Error('Failed to retrieve connect token');

                const nango = new Nango({ connectSessionToken: nangoToken });
                const klaviyoVerificationState = createNangoConnectVerificationState();

                // 使用 openConnectUI 打开授权界面
                nango.openConnectUI({
                    onEvent: (event: any) => {
                        console.log('Klaviyo onEvent:', event.type, event.payload?.connectionId);

                        if (event.type === 'connect') {
                            // 防止重复触发验证
                            if (klaviyoVerificationState.isVerifying || klaviyoVerificationState.hasSucceeded) {
                                console.log('Klaviyo: 验证已在进行中或已完成，跳过重复调用', {
                                    isVerifying: klaviyoVerificationState.isVerifying,
                                    hasSucceeded: klaviyoVerificationState.hasSucceeded
                                });
                                return;
                            }

                            klaviyoVerificationState.isVerifying = true;
                            console.log('Klaviyo: 开始验证集成创建状态，connectionId:', event.payload.connectionId);

                            // 连接成功，验证集成是否创建成功
                            checkIntegrationCreated('klaviyo', event.payload.connectionId)
                                .then(async () => {
                                    klaviyoVerificationState.hasSucceeded = true;
                                    klaviyoVerificationState.isVerifying = false;
                                    console.log('Klaviyo: 集成验证成功');
                                    await options?.onSuccess?.();
                                })
                                .catch(async (err) => {
                                    klaviyoVerificationState.isVerifying = false;
                                    console.error('Klaviyo: 集成验证失败', err);
                                    const restriction = resolveRestrictionFromConnectError(err);
                                    if (restriction) {
                                        await options?.onRestriction?.(restriction);
                                        return;
                                    }
                                    void options?.onError?.(err.message || 'Klaviyo connection verification failed');
                                });
                        } else if (event.type === 'error') {
                            void options?.onError?.(event.payload?.message || 'Klaviyo connection failed');
                        } else if (event.type === 'abort') {
                            void options?.onError?.('Klaviyo connection was cancelled');
                        } else if (event.type === 'close') {
                            // 用户关闭了授权弹窗（点击 finish）
                            // 如果已经开始验证或已成功，不视为错误
                            if (shouldIgnoreNangoConnectClose(klaviyoVerificationState)) {
                                console.log('Klaviyo: 用户点击 finish，验证正在进行或已完成，忽略 close 事件', {
                                    isVerifying: klaviyoVerificationState.isVerifying,
                                    hasSucceeded: klaviyoVerificationState.hasSucceeded
                                });
                                return;
                            }
                            // 只有在没有开始验证的情况下才视为错误
                            console.log('Klaviyo: 用户关闭弹窗但未开始验证，视为取消');
                            void options?.onError?.('Klaviyo connection dialog was closed');
                        }
                    }
                });

                // openConnectUI 不会返回结果，而是通过 onEvent 回调处理
                return {
                    uiOpened: true,
                    restrictionState: tokenBody.restrictionState ?? tokenBody.eligibilityState,
                    blockConnect: tokenBody.blockConnect,
                    upgradeRequired: tokenBody.upgradeRequired ?? false,
                    eligibilityState: tokenBody.eligibilityState,
                    allowsFreshTrial: tokenBody.allowsFreshTrial,
                    message: tokenBody.message ?? tokenBody.eligibilityMessage,
                    eligibilityMessage: tokenBody.eligibilityMessage,
                    trialRemainingDays: tokenBody.trialRemainingDays,
                    currentWorkspaceId: tokenBody.currentWorkspaceId,
                    currentWorkspaceName: tokenBody.currentWorkspaceName,
                };
            }

            case 'feishu': {
                const fallbackRedirectUri = (() => {
                    const url = new URL(window.location.href);
                    url.searchParams.set('connect_success', 'feishu');
                    url.searchParams.set('workspace_id', workspaceId);
                    url.searchParams.set('type', 'feishu');
                    return url.toString();
                })();

                const authWindow = openAuthorizationPopup('Failed to open Feishu authorization window. Please allow popups for this site.');

                let connectInit;
                try {
                    connectInit = await authService.initFeishuConnect({
                        workspace_id: workspaceId,
                        region: connectionConfig?.region,
                        provider: connectionConfig?.provider,
                        app_id: connectionConfig?.app_id,
                        client_id: connectionConfig?.client_id,
                        app_secret: connectionConfig?.app_secret,
                        client_secret: connectionConfig?.client_secret,
                        scope: connectionConfig?.scope,
                        redirect_uri: connectionConfig?.redirect_uri,
                        return_url: connectionConfig?.return_url ?? fallbackRedirectUri,
                        ...buildSceneRequest(connectionConfig),
                        axiosRequestConfig
                    });
                } catch (error) {
                    closeAuthorizationPopup(authWindow);
                    throw error;
                }

                if (connectInit.blockConnect) {
                    closeAuthorizationPopup(authWindow);
                    return {
                        success: false,
                        restrictionState: connectInit.restrictionState ?? connectInit.eligibilityState,
                        blockConnect: connectInit.blockConnect,
                        upgradeRequired: connectInit.upgradeRequired ?? false,
                        eligibilityState: connectInit.eligibilityState,
                        allowsFreshTrial: connectInit.allowsFreshTrial,
                        message: connectInit.message ?? connectInit.eligibilityMessage,
                        eligibilityMessage: connectInit.eligibilityMessage,
                        trialRemainingDays: connectInit.trialRemainingDays,
                        currentWorkspaceId: connectInit.currentWorkspaceId,
                        currentWorkspaceName: connectInit.currentWorkspaceName,
                    };
                }

                if (!connectInit.authorize_url) {
                    closeAuthorizationPopup(authWindow);
                    throw new Error(connectInit.message || 'Failed to initialize Feishu authorization URL');
                }

                authWindow.location.replace(connectInit.authorize_url);
                await options?.onAuthorizationStarted?.();
                try { authWindow.focus(); } catch {}

                try {
                    const popupResult = await waitForFeishuOAuthPopupResult(authWindow);
                    await checkIntegrationCreated('feishu', popupResult.connection || undefined);
                } catch (error) {
                    closeAuthorizationPopup(authWindow);
                    throw error;
                }

                closeAuthorizationPopup(authWindow);

                return {
                    success: true,
                    restrictionState: connectInit.restrictionState ?? connectInit.eligibilityState,
                    blockConnect: connectInit.blockConnect,
                    upgradeRequired: connectInit.upgradeRequired ?? false,
                    eligibilityState: connectInit.eligibilityState,
                    allowsFreshTrial: connectInit.allowsFreshTrial,
                    message: connectInit.message ?? connectInit.eligibilityMessage,
                    eligibilityMessage: connectInit.eligibilityMessage,
                    trialRemainingDays: connectInit.trialRemainingDays,
                    currentWorkspaceId: connectInit.currentWorkspaceId,
                    currentWorkspaceName: connectInit.currentWorkspaceName,
                };
            }

            case 'meta-ads':
            case 'facebook-page':
            case 'instagram':
            case 'linkedin-ads':
            case 'linkedinads':
            case 'linkedin':
            case 'pinterest-ads':
            case 'pinterestads':
            case 'pinterest':
            case 'x-ads':
            case 'xads':
            case 'twitter-ads':
            case 'twitterads':
            case 'twitter-x':
            case 'twitterx':
            case 'twitter':
            case 'x':
            case 'youtube-ads':
            case 'youtubeads':
            case 'youtube':
            case 'snapchat-ads':
            case 'snapchatads':
            case 'snapchat': {
                const popup = openAuthorizationPopup('Unable to open the authorization window. Please allow pop-ups for Bustly and try again.');

                let connectInit;
                try {
                    connectInit = await authService.initZernioConnect({
                        platform: normalizedProvider,
                        returnUrl: window.location.href,
                        axiosRequestConfig
                    });
                } catch (error) {
                    try { popup.close(); } catch {}
                    throw error;
                }

                if (connectInit.completed) {
                    try { popup.close(); } catch {}
                    await checkZernioIntegrationCreated(normalizedProvider, connectInit.flowId, axiosRequestConfig, {
                        maxAttempts: 10,
                    });
                    return { success: true, flowId: connectInit.flowId };
                }

                if (!connectInit.authUrl) {
                    try { popup.close(); } catch {}
                    throw new Error('We could not start the authorization flow. Please try again.');
                }

                const popupResult = waitForZernioOAuthPopupResult(popup);
                popup.location.replace(connectInit.authUrl);
                popup.focus();

                let stopZernioStatusPolling = false;
                const statusPromise = checkZernioIntegrationCreated(normalizedProvider, connectInit.flowId, axiosRequestConfig, {
                    maxAttempts: 300,
                    shouldStop: () => stopZernioStatusPolling,
                });
                const popupSignalPromise = popupResult
                    .then(() => 'popup-success' as const)
                    .catch((error) => {
                        const message = getErrorMessage(error) || '';
                        if (/window was closed|authorization was cancelled/i.test(message)) {
                            return 'popup-closed' as const;
                        }
                        throw error;
                    });

                try {
                    const winner = await Promise.race([
                        statusPromise.then(() => 'status' as const),
                        popupSignalPromise,
                    ]);

                    if (winner === 'status') {
                        return { success: true, flowId: connectInit.flowId };
                    }

                    if (winner === 'popup-success') {
                        await statusPromise;
                        return { success: true, flowId: connectInit.flowId };
                    }

                    const recoveredAfterPopupClose = await Promise.race([
                        statusPromise.then(() => true),
                        new Promise<boolean>(resolve => window.setTimeout(() => resolve(false), 45000)),
                    ]);

                    if (recoveredAfterPopupClose) {
                        return { success: true, flowId: connectInit.flowId, recoveredAfterPopupClose: true };
                    }

                    stopZernioStatusPolling = true;
                    void statusPromise.catch(() => undefined);
                    throw new Error('Authorization window was closed before the connection finished.');
                } catch (error) {
                    stopZernioStatusPolling = true;
                    void statusPromise.catch(() => undefined);
                    throw error;
                }
            }

            case 'insider-one':
            case 'insider-one-oauth': {
                return openGenericNangoOAuth({
                    integrationId: 'insider-one-oauth',
                    providerConfigKey: 'insider-one-oauth',
                    verificationProvider: 'insider-one-oauth',
                    displayName: 'Insider One',
                });
            }

            case 'emarsys':
            case 'emarsys-oauth': {
                return openGenericNangoOAuth({
                    integrationId: 'emarsys-oauth',
                    providerConfigKey: 'emarsys-oauth',
                    verificationProvider: 'emarsys-oauth',
                    displayName: 'Emarsys',
                });
            }

            case 'similarweb':
            case 'ahrefs':
            case 'omnisend':
            case 'okendo':
            case 'prestashop':
            case 'presta-shop':
            case 'figma':
            case 'insider-one-api-key': {
                const manualApiKeyProvider = MANUAL_API_KEY_PROVIDER[normalizedProvider] || normalizedProvider;
                const requestBody = {
                    provider: manualApiKeyProvider,
                    apiKey: connectionConfig?.apiKey,
                    accountLabel: connectionConfig?.accountLabel,
                    storeUrl: connectionConfig?.storeUrl,
                    ...buildSceneRequest(connectionConfig),
                };
                if (normalizedProvider === 'prestashop' || normalizedProvider === 'presta-shop') {
                    if (!requestBody.storeUrl || !requestBody.apiKey) {
                        throw new Error('PrestaShop store URL and Webservice key are required');
                    }
                } else if (!requestBody.apiKey) {
                    throw new Error('API key is required');
                }
                const result = await authService.connectManualApiKeyIntegration(requestBody, axiosRequestConfig);
                await fetchIntegrations(false, { forceRefresh: true });
                return result;
            }

            default:
                throw new Error(`Unsupported platform: ${platform}`);
        }
    }, [workspaceId, checkIntegrationCreated, checkZernioIntegrationCreated, buildSceneRequest, fetchIntegrations, updateConnectedIntegrationLabel]);

    /**
     * 断开平台集成
     * @param platform - 平台名称（如 'shopify', 'google-ads', 'slack'）
     */
    const disconnect = useCallback(async (platform: string, options?: DisconnectIntegrationOptions) => {
        if (!workspaceId) {
            throw new Error('Workspace ID is required');
        }

        const axiosRequestConfig: AxiosRequestConfig = { headers: { 'X-Workspace-Id': workspaceId } };

        // 标准化平台名称：同时处理空格和下划线
        const normalizedProvider = platform.toLowerCase().replace(/[\s_]+/g, '-');
        let removeProviderAfterDisconnect: string | null = null;

        switch (normalizedProvider) {
            case 'shopify': {
                // Shopify 断开 - 从 mappings 中获取店铺信息
                const shopifyIntegration = integrations.shopify;
                const firstMapping = shopifyIntegration?.mappings?.[0];
                const shopNameOrDomain = options?.shopDomain || firstMapping?.account_name || firstMapping?.connection_id;

                if (!shopNameOrDomain) {
                    throw new Error('Cannot disconnect Shopify: no shop domain found');
                }

                const shopDomain = shopNameOrDomain.includes('.myshopify.com')
                    ? shopNameOrDomain
                    : `${shopNameOrDomain}.myshopify.com`;

                await authService.disconnectShopify({
                    shop_domain: shopDomain,
                    axiosRequestConfig
                });
                removeProviderAfterDisconnect = normalizedProvider;
                break;
            }

            case 'google-ads':
            case 'google-analytics':
            case 'tiktok-ads':
            case 'google-search-console':
            case 'bigcommerce':
            case 'woocommerce':
            case 'magento':
            case 'adobe-commerce':
            case 'klaviyo':
            case 'mailchimp':
            case 'mailchimp-oauth':
            case 'semrush':
            case 'figma-oauth':
            case 'emarsys':
            case 'emarsys-oauth': {
                // Nango 集成的断开
                await authService.unbindNangoIntegration({
                    integration_id: PLATFORM_TO_NANGO_ID[normalizedProvider] || normalizedProvider,
                    connection_id: options?.connectionId,
                    axiosRequestConfig
                });
                break;
            }

            case 'feishu': {
                await authService.disconnectFeishu({
                    workspace_id: workspaceId,
                    connection_id: options?.connectionId,
                    axiosRequestConfig
                });
                removeProviderAfterDisconnect = 'feishu';
                break;
            }

            case 'slack': {
                await authService.disconnectSlack({
                    workspace_id: workspaceId
                });
                break;
            }

            case 'aliexpress': {
                // Aliexpress 断开
                await authService.unbindAliexpress({
                    axiosRequestConfig
                });
                break;
            }

            case 'wix': {
                // Wix 断开
                await authService.disconnectWix({
                    workspace_id: workspaceId,
                    axiosRequestConfig
                });
                break;
            }

            case 'meta-ads':
            case 'facebook-page':
            case 'instagram':
            case 'linkedin-ads':
            case 'linkedinads':
            case 'linkedin':
            case 'pinterest-ads':
            case 'pinterestads':
            case 'pinterest':
            case 'x-ads':
            case 'xads':
            case 'twitter-ads':
            case 'twitterads':
            case 'twitter-x':
            case 'twitterx':
            case 'twitter':
            case 'x':
            case 'youtube-ads':
            case 'youtubeads':
            case 'youtube':
            case 'snapchat-ads':
            case 'snapchatads':
            case 'snapchat': {
                const workspaceIntegrationId = options?.workspaceIntegrationId
                    || (Number.isFinite(Number(options?.connectionId)) ? options?.connectionId : undefined);
                await authService.disconnectZernioIntegration({
                    platform: normalizedProvider,
                    workspaceIntegrationId: workspaceIntegrationId == null ? undefined : Number(workspaceIntegrationId),
                    axiosRequestConfig
                });
                break;
            }

            case 'insider-one':
            case 'insider-one-oauth':
            case 'insider-one-api-key': {
                const isManualApiKeyConnection = String(options?.authType || '').toLowerCase() === 'api_key'
                    || options?.native === true
                    || normalizedProvider === 'insider-one-api-key';
                if (isManualApiKeyConnection) {
                    await authService.disconnectManualApiKeyIntegration('insider-one', axiosRequestConfig, options?.workspaceIntegrationId);
                    break;
                }

                await authService.unbindNangoIntegration({
                    integration_id: 'insider-one-oauth',
                    connection_id: options?.connectionId,
                    axiosRequestConfig
                });
                break;
            }

            case 'similarweb':
            case 'ahrefs':
            case 'omnisend':
            case 'okendo':
            case 'prestashop':
            case 'presta-shop': {
                const manualApiKeyProvider = MANUAL_API_KEY_PROVIDER[normalizedProvider] || normalizedProvider;
                await authService.disconnectManualApiKeyIntegration(manualApiKeyProvider, axiosRequestConfig, options?.workspaceIntegrationId);
                break;
            }

            case 'figma': {
                if (resolveFigmaDisconnectTransport(options) === 'manual-api-key') {
                    await authService.disconnectManualApiKeyIntegration(
                        'figma',
                        axiosRequestConfig,
                        options?.workspaceIntegrationId
                    );
                    break;
                }

                await authService.unbindNangoIntegration({
                    integration_id: 'figma',
                    connection_id: options?.connectionId,
                    axiosRequestConfig
                });
                break;
            }

            case 'judgeme':
            case 'judge.me':
            case 'judge-me': {
                await authService.disconnectJudgeMeOAuth({
                    workspace_id: workspaceId,
                    platform: 'judgeme',
                    connection_id: options?.connectionId,
                    integration_id: options?.workspaceIntegrationId,
                });
                break;
            }

            default:
                throw new Error(`Unsupported platform: ${platform}`);
        }

        const refreshedIntegrations = await fetchIntegrations(false, { forceRefresh: true });
        if (removeProviderAfterDisconnect && !isProviderConnected(refreshedIntegrations, removeProviderAfterDisconnect)) {
            removeIntegration(removeProviderAfterDisconnect);
        }
    }, [workspaceId, integrations, fetchIntegrations, removeIntegration]);

    useEffect(() => {
        if (!workspaceId || typeof window === 'undefined') return;
        const result = parseJudgeMeOAuthResult(window.location.href);
        if (!result) return;

        const url = new URL(window.location.href);
        url.searchParams.delete('judgeme_oauth');
            url.searchParams.delete('judgeme_platform');
            url.searchParams.delete('judgeme_connection');
            url.searchParams.delete('judgeme_account');
            url.searchParams.delete('judgeme_error');
            url.searchParams.delete('judgeme_flow');
            window.history.replaceState(window.history.state, document.title, `${url.pathname}${url.search}${url.hash}`);

        if (result.connected) {
            void fetchIntegrations(false, { forceRefresh: true });
        } else if (result.error) {
            setError(new Error(result.error));
        }
    }, [workspaceId, fetchIntegrations]);

    return {
        integrations,
        allIntegrations,
        isLoading,
        hasFetchedCurrentWorkspace,
        error,
        refresh: fetchIntegrations,
        isConnected,
        getIntegration,
        connect,
        disconnect,
        hasConnectedIntegrations,
    };
};

/**
 * 工具函数：将 UI 中的平台名称转换为 provider 格式
 */
export const normalizePlatformName = (name: string): string => {
    return name.toLowerCase().replace(/\s+/g, '-');
};
